// @ts-nocheck
import { PayloadAction } from '@reduxjs/toolkit';
import { RawAxiosRequestHeaders } from 'axios';
import { Dispatch } from 'redux';

import {
  createBatchSalesInfo,
  calculateTotalAmount,
  calculateTotalOriginalAmount,
  createBatchPaymentSuccessPayload,
  extractZipCode,
} from './batchPaymentHelpers';
import {
  getApplePayPaymentPayloadForCpServer,
  getBankPaymentPayloadForCpServer,
  getCCPaymentPayloadForCpServer,
  getNanopayBankPaymentPayloadForCpServer,
  getZipCode,
} from './cpServerTranslators';
import {
  isAmexAccountEnabled,
  schedulePaymentHelpers,
  subscriptionPaymentHelpers,
} from './helpers';
import { closePayPalFlow, getPayPalCheckOut, openPayPalSandBox, startPayPalFlow } from './payPal';
import { lockForriskProfileToken, reportPrePayment } from './riskActions';

import { reportPaymentNewSyntax } from 'businessLogic/Insight/Reporting';
import {
  isApplePayEnabled as isApplePayEnabledBL,
  makeApplePayPayment as openApplePayPaymentBL,
} from 'businessLogic/Payment/ApplePay';
import { ApplePayInfo } from 'businessLogic/Payment/ApplePay/ApplePayTypes';
import { closeAllPayPalButtons, generatePaymentSucessPayload } from 'businessLogic/Payment/Common';
import { displayGratuityFeature } from 'businessLogic/Payment/Gratuity';
import { isMAIPasPDF, getPayorEmail } from 'businessLogic/Payment/helpers';
import { getHeadersForSSRRequest, logIfMissingriskProfileToken } from 'businessLogic/utils';
import * as batchPaymentLogs from 'businessLogic/utils/batchPaymentLogs';
import CreditCardSurchargingModal from 'components/Experiments/creditCardSurcharging/creditCardSurchargingModal';
import { isSurchargePaintedDoorOnForSale } from 'components/Shared/creditCardSurcharging/creditCardSurcharging';
import Logger from 'reporting/Logger';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import convertLocaleToCountry from 'server/helpers/convertLocaleToCountry';
import { isAppleMobileProduct, isMobile } from 'server/helpers/deviceInfo';
import { getUUID } from 'server/helpers/uuidGenerator';
import { adyenPaymentStatusTranslator } from 'server/services/serviceUtils';
import {
  formatMaskedAccountNumber,
  isApplePayPaymentMethod,
  isAffirmBnplAmountEligible,
  isBankAccountPaymentMethod,
  isCreditCardPaymentMethod,
  isNanopayPaymentMethod,
  isPayPalPaymentMethod,
  PaymentAmountValidator,
} from 'shared/PaymentHelpers';
import { mark } from 'shared/clientUtils';
import { promiseTimeout } from 'shared/timeoutPromise';
import { amountToCents } from 'shared/utils';
import {
  frictionlessCheckoutSelector,
  isFrictionlessCheckoutDataInvalidSelector,
  isFrictionlessCheckoutEmailValidSelector,
  isFrictionlessCheckoutPhoneValidSelector,
  isFrictionlessUserNameAvailableSelector,
  isFrictionlessCheckoutCompletedSelector,
} from 'store/auth/selectors';
import { authActions } from 'store/auth/slice';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import {
  isFrictionlessPhase2SubscriptionsEnabled,
  isBatchPaymentsEnabled,
} from 'store/ixp/selector';
import { ixpActions } from 'store/ixp/slice';
import { modalActions } from 'store/modal/slice';
import {
  saleSelectors,
  isForcedAutopayRecurringSelector,
  isOrphanedInvoiceSelector,
} from 'store/sale/selectors';
import { updateNewBalance, updateSubscriptionInfo } from 'store/sale/slice';
import { RootState } from 'store/store';
import { sliceFactory, thunkActionFactory } from 'store/utils';
import { networkPaymentErrorTranslator } from 'store/wallet/errorTranslators';
import {
  fetchWallets,
  REMOTE_BUTTON_ACCESS_FUNCTIONALITY,
  walletActions,
} from 'store/wallet/slice';
import { IBusinessLogic, PbcWalletInfo } from 'types/BusinessLogic';
import { CompanyInfo } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { ErrorWithResponse } from 'types/Error';
import { FeatureFlags } from 'types/FeatureFlags';
import { Nanopay } from 'types/Nanopay';
import { SelectedBank } from 'types/PayByBank';
import { Payment } from 'types/Payment';
import { Sale } from 'types/Sale';
import { UserWalletBank, UserWalletCard, Wallet } from 'types/Wallet';
import {
  CardType,
  PAYMENT_MAP,
  PaymentMethod,
  TXN_MAP,
  WALLET_MAP,
  PAYMENT_ERROR_TYPES,
  PAYMENT_DECLINE_REASON_CODES,
  CP_ERROR_CODES,
} from 'types/constants';
const splunkReporter = SplunkReporter.getInstance();
const wavefrontReporter = WavefrontReporter.getInstance();

const logger = 'store/payment/slice.ts';

/**
 * Extracts PII-safe error information from payment errors.
 * Only returns predefined enum values - never raw error messages.
 */
type PaymentErrorInfo = {
  errorCode: string;
  errorType: string;
  reasonCode: string | undefined;
  httpStatusCode: number | undefined;
};

const extractPaymentErrorInfo = (error: ErrorWithResponse): PaymentErrorInfo => {
  const response = error?.response || error;
  const data = response?.data || {};
  const status = response?.status;

  // Extract errorCode from CP_ERROR_CODES or default to 'unknown'
  let errorCode = 'unknown';
  if (data?.errorCode) {
    // Validate it's a known CP error code
    const knownCpCodes = Object.values(CP_ERROR_CODES);
    errorCode = knownCpCodes.includes(data.errorCode) ? data.errorCode : 'unknown';
  }

  // Extract errorType from PAYMENT_ERROR_TYPES enum
  let errorType = PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR;
  const knownErrorTypes = Object.values(PAYMENT_ERROR_TYPES);

  if (data?.message && knownErrorTypes.includes(data.message)) {
    errorType = data.message;
  } else if (data?.paymentStatus === PAYMENT_ERROR_TYPES.DECLINED) {
    errorType = PAYMENT_ERROR_TYPES.DECLINED;
  } else if (typeof status === 'number' && status >= 500) {
    errorType = PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR;
  }

  // Extract reasonCode from PAYMENT_DECLINE_REASON_CODES enum
  // Only log known reason codes to prevent PII leakage from unexpected server values.
  // Unknown codes are set to 'unknown' so we have visibility that a code existed but wasn't recognized.
  let reasonCode: string | undefined;
  const outcomeReasonCode = data?.outcome?.reasonCode;
  if (outcomeReasonCode) {
    const knownReasonCodes = Object.values(PAYMENT_DECLINE_REASON_CODES);
    reasonCode = knownReasonCodes.includes(outcomeReasonCode) ? outcomeReasonCode : 'unknown';
  }

  // Extract httpStatusCode (numeric status code is PII-safe)
  const httpStatusCode = typeof status === 'number' ? status : undefined;

  return {
    errorCode,
    errorType,
    reasonCode,
    httpStatusCode,
  };
};

/**
 * Helper function to check if saveToMerchantWalletStatus contains actual error content
 * (not just an empty error object which is a server bug)
 */
const hasActualErrorContent = (saveToMerchantWalletStatus: any): boolean => {
  return (
    saveToMerchantWalletStatus?.error &&
    (saveToMerchantWalletStatus.error.message ||
      saveToMerchantWalletStatus.error.stack ||
      (Object.keys(saveToMerchantWalletStatus.error).length > 0 &&
        Object.values(saveToMerchantWalletStatus.error).some((v) => v)))
  );
};

const initialState = {
  gratuityValue: 0,
  allowOverPay: true,
  paymentStatus: null,
  requestIdInProgress: null,
  paymentError: null,
  affirmMinTxnLimit: undefined,
  affirmMaxTxnLimit: undefined,
  paymentSessionCounter: 0,
  isPaymentSessionDirty: false,
  successfulPaymentCounter: 0,
  lastSuccessfulPaymentResponse: null,
  urlToInvoicePage: null,
  paidInvoices: null,
  isPayPalCommerceInvoice: false,
  isDateScheduled: false,
  isAutoPayOn: false,
  contactDetailsName: '',
  contactDetailsEmail: '',
  isContactDetailsNameValid: true,
  isContactDetailsEmailValid: true,
  bankList: [],
  selectedBank: null,
  paypalExpressCheckoutPaymentMethod: '',
  shouldTriggerAmountEdit: false,
  creditFloorDeclineAmount: null,
  isPaymentRetry: false,
  originalPaymentAmount: null,
  originalPaymentMethod: null,
  paymentFailureCount: 0,
  lastPaymentWasRetry: false,
} as Payment;

const { reducer, actions } = sliceFactory({
  name: 'payment',
  initialState,
  reducers: {
    gratuityAmountChange(
      state: Pick<Payment, 'inputAmount' | 'paymentMethodType' | 'allowOverPay' | 'balanceAmount'>,
      action: PayloadAction<{
        gratuityAmount: number;
        region: CompanyInfo['region'];
        featureFlags: FeatureFlags;
        amountConfig: Config['amountConfig'];
      }>
    ) {
      let {
        payload: { gratuityAmount = 0, region, featureFlags, amountConfig },
      } = action;

      const {
        inputAmount,
        paymentMethodType,
        allowOverPay,
        balanceAmount,
        affirmMinTxnLimit,
        affirmMaxTxnLimit,
      } = state;

      let total = 0;
      let validGratuityAmount = 0;
      let isAmountValid = false;
      try {
        validGratuityAmount = Number(gratuityAmount.toFixed(2));
        total = Number((inputAmount + validGratuityAmount).toFixed(2));
        const amountData = {
          amount: inputAmount,
          balanceAmount: balanceAmount,
          paymentMethod: paymentMethodType,
          allowOverPay: allowOverPay,
          gratuityValue: validGratuityAmount,
          transactionType: undefined,
          region,
          featureFlags,
          amountConfig,
          affirmMinTxnLimit,
          affirmMaxTxnLimit,
        };
        isAmountValid = PaymentAmountValidator.validate(amountData);
      } catch (error) {
        return state;
      }

      return {
        ...state,
        gratuityValue: validGratuityAmount,
        isAmountValid,
        amount: total,
      };
    },
    paymentAmountChange(
      state: Pick<Payment, 'paymentMethodType' | 'allowOverPay' | 'gratuityValue' | 'inputAmount'>,
      action: PayloadAction<{
        balanceAmount: number;
        newAmount: number;
        region: CompanyInfo['region'];
        featureFlags: FeatureFlags;
        transactionType: Sale['type'];
        amount: Sale['amount'];
        amountConfig: Config['amountConfig'];
        isBatchPayments?: boolean;
      }>
    ) {
      const {
        payload: {
          balanceAmount,
          newAmount,
          region,
          featureFlags,
          transactionType,
          amount,
          amountConfig,
          isBatchPayments,
        },
      } = action;
      const {
        paymentMethodType,
        allowOverPay,
        gratuityValue,
        affirmMinTxnLimit,
        affirmMaxTxnLimit,
      } = state;
      const amountData = {
        amount: newAmount,
        balanceAmount: balanceAmount,
        paymentMethod: paymentMethodType,
        allowOverPay: allowOverPay,
        gratuityValue: gratuityValue,
        transactionType,
        region,
        featureFlags,
        saleAmount: amount,
        amountConfig,
        isBatchPayments,
        affirmMinTxnLimit,
        affirmMaxTxnLimit,
      };

      if (state.inputAmount === newAmount) {
        return state;
      }

      const isAmountValid = PaymentAmountValidator.validate(amountData);
      const inputAmount = newAmount;
      return {
        ...state,
        amount: inputAmount,
        inputAmount,
        isAmountValid,
      };
    },
    defaultPaymentAmountChange(
      state: Pick<Payment, 'defaultInputAmount'>,
      action: PayloadAction<Pick<Payment, 'defaultInputAmount'>>
    ) {
      state.defaultInputAmount = action.payload.defaultInputAmount;
    },
    paymentMethodTypeChange(
      state: Pick<
        Payment,
        | 'defaultInputAmount'
        | 'inputAmount'
        | 'allowOverPay'
        | 'gratuityValue'
        | 'paymentMethodSubType'
      >,
      action: PayloadAction<{
        paymentMethodType?: Payment['prevPaymentMethodType'];
        newPaymentMethodType?: Payment['prevPaymentMethodType'];
        balanceAmount: number;
        paymentMethodSubType: PaymentMethod['paymentMethodSubType'];
        region: CompanyInfo['region'];
        featureFlags: FeatureFlags;
        transactionType: Sale['type'];
        amount: Sale['amount'];
        amountConfig: Config['amountConfig'];
        isBatchPayments?: boolean;
      }>
    ) {
      const {
        newPaymentMethodType,
        paymentMethodType,
        balanceAmount,
        paymentMethodSubType,
        region,
        featureFlags,
        transactionType,
        amount,
        amountConfig,
        isBatchPayments,
      } = action.payload;

      const amountData = {
        amount: state.inputAmount,
        balanceAmount,
        paymentMethod: newPaymentMethodType,
        allowOverPay: state.allowOverPay,
        gratuityValue: state.gratuityValue,
        transactionType,
        region,
        featureFlags,
        saleAmount: amount,
        amountConfig,
        isBatchPayments,
        affirmMinTxnLimit: state?.affirmMinTxnLimit,
        affirmMaxTxnLimit: state?.affirmMaxTxnLimit,
      };
      return {
        ...state,
        paymentError: null,
        paymentMethodType: newPaymentMethodType,
        prevPaymentMethodType: paymentMethodType,
        isAmountValid: PaymentAmountValidator.validate(amountData),
        paymentMethodSubType,
      };
    },
    setScheduledDate(
      state: Pick<Payment, 'scheduledDate' | 'isDateScheduled'>,
      action: PayloadAction<{ scheduledDate: Date; isDateScheduled: boolean }>
    ) {
      const { scheduledDate, isDateScheduled } = action.payload;
      state.scheduledDate = scheduledDate;
      state.isDateScheduled = isDateScheduled;
    },
    setSelectedBank(
      state: Pick<Payment, 'selectedBank'>,
      action: PayloadAction<{ selectedBank: SelectedBank }>
    ) {
      const { selectedBank } = action.payload;
      state.selectedBank = selectedBank;
    },
    setPayPalCommerceIdempotentKeys(
      state: Pick<Payment, 'ppCreateOrderKey' | 'ppCaptureOrderKey'>,
      action: PayloadAction<Pick<Payment, 'ppCreateOrderKey' | 'ppCaptureOrderKey'>>
    ) {
      state.ppCreateOrderKey = action.payload.ppCreateOrderKey;
      state.ppCaptureOrderKey = action.payload.ppCaptureOrderKey;
    },
    rejectPaymentRequest(
      state: Pick<Payment, 'paymentRejectType'>,
      action: PayloadAction<Payment['paymentRejectType'] | undefined>
    ) {
      state.paymentRejectType = action.payload;
    },
    createPayment(
      state: Pick<
        Payment,
        'paymentError' | 'paymentStatus' | 'isResumingPayment' | 'creditFloorDeclineAmount'
      >
    ) {
      state.paymentError = null;
      state.paymentStatus = TXN_MAP.STATUS.IN_PROGRESS;
      state.isResumingPayment = false; // Clear resuming flag when payment actually starts
      state.creditFloorDeclineAmount = null; // Clear credit floor decline amount on new payment attempt
    },
    createPaymentError(
      state: Pick<Payment, 'paymentSessionCounter' | 'inputAmount'>,
      action: PayloadAction<{ error: ErrorWithResponse; featureFlags?: FeatureFlags }>
    ) {
      let { error, featureFlags } = action.payload;
      if (error && error.response) {
        error = error.response;
      }
      const { status = {}, data = {} } = error;
      const translatedError = networkPaymentErrorTranslator({ status, data, featureFlags });

      // Store the failed payment amount for credit floor decline messages
      const isCreditFloorDecline =
        (translatedError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT ||
          translatedError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT_V2 ||
          translatedError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.INFO_INCORRECT ||
          translatedError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.INFO_INCORRECT_V2) &&
        data?.paymentStatus === TXN_MAP.STATUS.DECLINED;

      return {
        ...state,
        paymentStatus:
          data && data.paymentStatus === TXN_MAP.STATUS.DECLINED
            ? data.paymentStatus
            : TXN_MAP.STATUS.ERROR,
        paymentError: translatedError,
        paymentSessionCounter: state.paymentSessionCounter + 1,
        // Only set creditFloorDeclineAmount on credit floor decline, preserve existing value otherwise
        creditFloorDeclineAmount: isCreditFloorDecline ? state.inputAmount : null,
      };
    },
    tokenizeWallet(
      state: Pick<Payment, 'paymentError' | 'paymentStatus' | 'creditFloorDeclineAmount'>
    ) {
      state.paymentError = null;
      state.paymentStatus = TXN_MAP.STATUS.IN_PROGRESS;
      state.creditFloorDeclineAmount = null; // Clear credit floor decline amount on new payment attempt
    },
    tokenizeWalletError(
      state: Pick<Payment, 'paymentError' | 'paymentStatus'>,
      action: PayloadAction<{ error: Payment['paymentError'] }>
    ) {
      state.paymentError = action.payload.error;
      state.paymentStatus = TXN_MAP.STATUS.ERROR;
    },
    validateContactDetailsName(
      state: Pick<Payment, 'isContactDetailsNameValid'>,
      action: PayloadAction<Payment['isContactDetailsNameValid']>
    ) {
      state.isContactDetailsNameValid = action.payload;
    },
    validateContactDetailsEmail(
      state: Pick<Payment, 'isContactDetailsEmailValid'>,
      action: PayloadAction<Payment['isContactDetailsEmailValid']>
    ) {
      state.isContactDetailsEmailValid = action.payload;
    },
    cancelPayment(
      state: Pick<
        Payment,
        'paymentError' | 'paymentStatus' | 'isResumingPayment' | 'creditFloorDeclineAmount'
      >
    ) {
      state.paymentError = null;
      state.paymentStatus = null;
      state.isResumingPayment = false; // Clear resuming flag when payment is cancelled
      state.creditFloorDeclineAmount = null; // Clear credit floor decline amount
    },
    setPaymentAmountEditMode(
      state: Pick<Payment, 'shouldTriggerAmountEdit'>,
      action: PayloadAction<boolean>
    ) {
      state.shouldTriggerAmountEdit = action.payload;
    },
    createSchedulePaymentSuccess(
      state: Pick<
        Payment,
        'paymentSessionCounter' | 'successfulPaymentCounter' | 'creditFloorDeclineAmount'
      >,
      action: PayloadAction<
        { result: { data: Payment['lastSuccessfulSchedulePaymentResponse'] } } | undefined
      >
    ) {
      // This function gets the payment result and object that contain the account number in case of form
      const { data } = (action.payload && action.payload.result) || {};

      return {
        ...state,
        paymentStatus: TXN_MAP.STATUS.SUCCESS,
        paymentError: null,
        paymentSessionCounter: state.paymentSessionCounter + 1,
        successfulPaymentCounter: state.successfulPaymentCounter + 1,
        lastSuccessfulSchedulePaymentResponse: data,
        creditFloorDeclineAmount: null, // Clear credit floor decline amount on success
      };
    },
    storePaidInvoices(
      state: Pick<Payment, 'paidInvoices'>,
      action: PayloadAction<Pick<Payment, 'paidInvoices'>>
    ) {
      state.paidInvoices = action.payload.paidInvoices;
    },
    createPaymentSuccess(
      state: Pick<
        Payment,
        | 'paymentMethodType'
        | 'successfulPaymentCounter'
        | 'paymentSessionCounter'
        | 'creditFloorDeclineAmount'
        | 'isPaymentRetry'
        | 'originalPaymentAmount'
        | 'originalPaymentMethod'
        | 'paymentFailureCount'
        | 'lastPaymentWasRetry'
      >,
      action: PayloadAction<{
        result: {
          data: {
            maskedAccountNumber?: string;
            maskedCardNumber?: string;
            cardType?: CardType;
            bankShortLivedToken?: string;
            cardShortLivedToken?: string;
            amountPaid: number;
          };
          config: {
            headers: {
              intuit_tid: string;
            };
          };
        };
      }>
    ) {
      const successParams = {
        payload: action.payload,
        paymentMethodType: state.paymentMethodType,
        successfulPaymentCounter: state.successfulPaymentCounter,
        paymentSessionCounter: state.paymentSessionCounter,
      };
      const paymentPayload = generatePaymentSucessPayload(successParams);

      return {
        ...state,
        ...paymentPayload,
        creditFloorDeclineAmount: null, // Clear credit floor decline amount on success
        lastPaymentWasRetry: state.isPaymentRetry, // Preserve retry state for success tracking
        isPaymentRetry: false,
        originalPaymentAmount: null,
        originalPaymentMethod: null,
        paymentFailureCount: 0,
      };
    },
    hideSuccessScreen(state: Pick<Payment, 'lastSuccessfulPaymentResponse'>) {
      state.lastSuccessfulPaymentResponse = 'back_from_success';
    },
    setPaymentRetryState(
      state: Pick<Payment, 'isPaymentRetry' | 'originalPaymentAmount' | 'originalPaymentMethod'>,
      action: PayloadAction<{
        isPaymentRetry: boolean;
        originalPaymentAmount?: number | null;
        originalPaymentMethod?: string | null;
      }>
    ) {
      const { isPaymentRetry, originalPaymentAmount, originalPaymentMethod } = action.payload;
      state.isPaymentRetry = isPaymentRetry;
      if (originalPaymentAmount !== undefined) {
        state.originalPaymentAmount = originalPaymentAmount;
      }
      if (originalPaymentMethod !== undefined) {
        state.originalPaymentMethod = originalPaymentMethod;
      }
    },
    incrementPaymentFailureCount(state: Pick<Payment, 'paymentFailureCount'>) {
      state.paymentFailureCount = (state.paymentFailureCount || 0) + 1;
    },
    resetPaymentRetryState(
      state: Pick<
        Payment,
        'isPaymentRetry' | 'originalPaymentAmount' | 'originalPaymentMethod' | 'paymentFailureCount'
      >
    ) {
      state.isPaymentRetry = false;
      state.originalPaymentAmount = null;
      state.originalPaymentMethod = null;
      state.paymentFailureCount = 0;
    },
    setIsAutoPayOn(
      state: Pick<Payment, 'isAutoPayOn'>,
      action: PayloadAction<Payment['isAutoPayOn']>
    ) {
      state.isAutoPayOn = action.payload;
    },
    setPayPalOrderId(
      state: Pick<Payment, 'intuitOrderId'>,
      action: PayloadAction<{ intuitOrderId: Payment['intuitOrderId'] } | undefined>
    ) {
      const { intuitOrderId } = (action && action.payload) || {};
      if (intuitOrderId) {
        state.intuitOrderId = intuitOrderId;
      }
    },
    disablePartialAndOverPay(state: Pick<Payment, 'allowOverPay' | 'partialPaymentEnabled'>) {
      state.allowOverPay = false;
      state.partialPaymentEnabled = false;
    },
    setContactDetailsName(
      state: Pick<Payment, 'contactDetailsName'>,
      action: PayloadAction<Payment['contactDetailsName']>
    ) {
      state.contactDetailsName = action.payload;
    },
    setContactDetailsEmail(
      state: Pick<Payment, 'contactDetailsEmail'>,
      action: PayloadAction<Payment['contactDetailsEmail']>
    ) {
      state.contactDetailsEmail = action.payload;
    },
    shouldShowPaypalAndVenmo(
      state: Pick<Payment, 'paymentMethodType'>,
      action: PayloadAction<{ paymentMethodType: Payment['paymentMethodType'] }>
    ) {
      const { paymentMethodType } = (action && action.payload) || {};
      state.paymentMethodType = paymentMethodType;
    },
    setIsSavePaymentMethodChecked(
      state: Pick<Payment, 'isSavePaymentMethodChecked' | 'paymentRejectType'>,
      action: PayloadAction<Payment['isSavePaymentMethodChecked']>
    ) {
      state.isSavePaymentMethodChecked = action.payload;
      state.paymentRejectType = null;
    },
    setSubscriptionUpdateRequired(
      state: Pick<Payment, 'isSubscriptionUpdatePaymentMethodRequired'>,
      action: PayloadAction<Payment['isSubscriptionUpdatePaymentMethodRequired']>
    ) {
      state.isSubscriptionUpdatePaymentMethodRequired = action.payload;
    },
    updateCountry(state: Pick<Payment, 'country'>, action: PayloadAction<Payment['country']>) {
      state.country = action.payload;
    },
    setPaypalExpressCheckoutPaymentMethod(
      state: Pick<Payment, 'paypalExpressCheckoutPaymentMethod'>,
      action: PayloadAction<Payment['paypalExpressCheckoutPaymentMethod']>
    ) {
      state.paypalExpressCheckoutPaymentMethod = action.payload;
    },
    setPendingPayment(
      state: Pick<Payment, 'isPendingPayment'>,
      action: PayloadAction<{ isPending: boolean }>
    ) {
      state.isPendingPayment = action.payload.isPending;
    },
    setResumingPayment(
      state: Pick<Payment, 'isResumingPayment'>,
      action: PayloadAction<{ isResuming: boolean }>
    ) {
      state.isResumingPayment = action.payload.isResuming;
    },
    setPaymentStatus(
      state: Pick<Payment, 'paymentStatus'>,
      action: PayloadAction<Payment['paymentStatus']>
    ) {
      state.paymentStatus = action.payload;
    },
    setPaymentProcessingMessage(
      state: Pick<Payment, 'paymentStatus' | 'paymentError'>,
      action: PayloadAction<string>
    ) {
      state.paymentStatus = TXN_MAP.STATUS.IN_PROGRESS;
      state.paymentError = { message: action.payload };
    },
  },
});

export const paymentReducer = reducer;
export const paymentActions = actions;

export const gratuityAmountChange = thunkActionFactory<number | undefined>(
  ({ payload = 0, dispatch, state }) => {
    const {
      featureFlags,
      companyInfo: { region },
      config,
    } = state;
    const gratuityValue = featureFlags && featureFlags['gratuity-enabled'] ? payload : 0;
    const amountConfig = config?.amountConfig;

    dispatch(
      paymentActions.gratuityAmountChange({
        gratuityAmount: gratuityValue,
        region,
        featureFlags,
        amountConfig,
      })
    );
  }
);

function refreshAffirmBnplPromoForAmount(state: any, amount: number) {
  try {
    const enabledPaymentMethods = state?.wallet?.enabledPaymentMethods;
    if (Array.isArray(enabledPaymentMethods) && !enabledPaymentMethods.includes('affirm_bnpl'))
      return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const cents = amountToCents(amount);
    // Only refresh when the amount actually changed (use Redux state).
    const prevCents = amountToCents(state?.payment?.inputAmount ?? state?.payment?.amount ?? 0);
    if (prevCents === cents) return;

    const paymentSectionPromoEl = document.getElementById('affirm-as-low-as-payment-section');
    const payOverTimePromoEl = document.getElementById('affirm-as-low-as-pay-over-time');

    const { affirmMinTxnLimit, affirmMaxTxnLimit } = state?.payment ?? {};
    const isEligible = isAffirmBnplAmountEligible(amount, affirmMinTxnLimit, affirmMaxTxnLimit);

    const wasVisible = paymentSectionPromoEl?.style.display !== 'none';

    if (!isEligible) {
      // Hide both promo elements, clear content, and skip API call
      if (paymentSectionPromoEl) {
        paymentSectionPromoEl.style.display = 'none';
        paymentSectionPromoEl.innerHTML = '';
      }
      if (payOverTimePromoEl) {
        payOverTimePromoEl.style.display = 'none';
        payOverTimePromoEl.innerHTML = '';
      }
      if (wasVisible) {
        splunkReporter.contextual({
          logInfo: { logLevel: 'info', logger },
          event: 'view',
          action: 'affirmBnplPromoVisibility',
          activityInfo: {
            visibility: 'hidden',
            reason: 'amount_ineligible',
            amount,
            amountCents: cents,
            affirmMinTxnLimit,
            affirmMaxTxnLimit,
            promoElements: {
              paymentSection: !!paymentSectionPromoEl,
              payOverTime: !!payOverTimePromoEl,
            },
          },
        });
      }
      return;
    }

    if (!wasVisible) {
      if (paymentSectionPromoEl) paymentSectionPromoEl.style.display = 'none';
      if (payOverTimePromoEl) payOverTimePromoEl.style.display = 'none';
    }

    // Update amount for the API call
    if (paymentSectionPromoEl) {
      paymentSectionPromoEl.setAttribute('data-amount', String(cents));
    }

    const w = window as any;
    const ui = w?.affirm?.ui;
    ui?.ready?.(() => {
      // Use MutationObserver to show elements after content is updated
      if (!wasVisible && paymentSectionPromoEl) {
        const observer = new MutationObserver(() => {
          observer.disconnect();
          paymentSectionPromoEl.style.display = '';
          if (payOverTimePromoEl) payOverTimePromoEl.style.display = '';
          splunkReporter.contextual({
            logInfo: { logLevel: 'info', logger },
            event: 'view',
            action: 'affirmBnplPromoVisibility',
            activityInfo: {
              visibility: 'shown',
              reason: 'amount_eligible',
              amount,
              amountCents: cents,
              affirmMinTxnLimit,
              affirmMaxTxnLimit,
              promoElements: {
                paymentSection: !!paymentSectionPromoEl,
                payOverTime: !!payOverTimePromoEl,
              },
            },
          });
        });
        observer.observe(paymentSectionPromoEl, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
      ui?.refresh();
    });
  } catch (error) {
    splunkReporter.contextual({
      logInfo: {
        logLevel: 'error',
        logger,
      },
      event: 'view',
      action: 'affirmBnplPromoError',
      error,
    });
  }
}

export const paymentAmountChange = thunkActionFactory<number>(({ payload, dispatch, state }) => {
  const {
    sale = {} as Sale,
    insight,
    companyInfo: { region },
    featureFlags,
    config,
  } = state;
  const balanceAmount = saleSelectors.balanceSelector(sale);
  const transactionType = saleSelectors.typeSelector(sale);
  const amount = saleSelectors.amountSelector(sale);
  const amountConfig = config?.amountConfig;

  // Check if batch payments is enabled and determine when to skip validation:
  // - Skip validation ONLY for: multiple invoices OR single non-default invoice (when amount > 0)
  // - Apply validation for: no invoices (amount = 0) OR only default invoice selected
  const selectedBatchInvoices = insight ? insightSelectors.selectedBatchInvoices(insight) : [];
  const onlyDefaultInvoiceSelected =
    selectedBatchInvoices?.length === 1 && selectedBatchInvoices[0]?.defaultInvoice;
  const isBatchPayments = isBatchPaymentsEnabled(state) && !onlyDefaultInvoiceSelected;

  dispatch(
    paymentActions.paymentAmountChange({
      newAmount: payload,
      balanceAmount,
      region,
      featureFlags,
      transactionType,
      amount,
      amountConfig,
      isBatchPayments,
    })
  );

  refreshAffirmBnplPromoForAmount(state, payload);
});

export const paymentAmountReset = thunkActionFactory(({ dispatch, state }) => {
  const {
    sale,
    payment: { defaultInputAmount },
    companyInfo: { region },
    featureFlags,
    config,
  } = state;

  const balanceAmount = saleSelectors.balanceSelector(sale);
  const transactionType = saleSelectors.typeSelector(sale);
  const amount = saleSelectors.amountSelector(sale);

  dispatch(
    paymentActions.paymentAmountChange({
      newAmount: defaultInputAmount,
      balanceAmount: balanceAmount,
      region,
      featureFlags,
      transactionType,
      amount,
      amountConfig: config?.amountConfig,
    })
  );

  refreshAffirmBnplPromoForAmount(state, defaultInputAmount);
});

export const setDefaultInputAmount = thunkActionFactory(({ dispatch, state }) => {
  const {
    companyInfo: { region },
    sale,
    payment: { paymentMethodType, allowOverPay, affirmMaxTxnLimit },
    featureFlags,
    config,
  } = state;

  const balanceAmount = saleSelectors.balanceSelector(sale);
  const amountConfig = config?.amountConfig;

  dispatch(
    paymentActions.defaultPaymentAmountChange({
      defaultInputAmount: getDefaultPaymentAmount({
        balanceAmount,
        paymentMethodType,
        allowOverPay,
        region,
        featureFlags,
        amountConfig,
        affirmMaxTxnLimit,
      }),
    })
  );
});

const getDefaultPaymentAmount = ({
  balanceAmount,
  paymentMethodType,
  allowOverPay,
  region,
  featureFlags,
  amountConfig,
  affirmMaxTxnLimit,
}: {
  balanceAmount: number;
  paymentMethodType: Payment['paymentMethodType'];
  allowOverPay: Payment['allowOverPay'];
  region: CompanyInfo['region'];
  featureFlags: FeatureFlags;
  amountConfig: Config['amountConfig'];
  affirmMaxTxnLimit?: Payment['affirmMaxTxnLimit'];
}) => {
  const maxPaymentAmount = PaymentAmountValidator.getMaxPaymentAmount(
    balanceAmount,
    paymentMethodType,
    allowOverPay,
    region,
    featureFlags,
    amountConfig,
    affirmMaxTxnLimit
  );
  return Math.min(balanceAmount, maxPaymentAmount);
};

export const paymentMethodTypeChange = thunkActionFactory<{
  paymentMethodType: Payment['paymentMethodType'];
  paymentMethodSubType?: Payment['paymentMethodSubType'];
}>(({ payload, dispatch, state }) => {
  const { paymentMethodType: newPaymentMethodType, paymentMethodSubType: newPaymentMethodSubType } =
    payload;
  const {
    sale,
    insight,
    payment: { paymentMethodType, isDateScheduled },
    companyInfo: { region },
    featureFlags,
    config,
  } = state;
  const amountConfig = config?.amountConfig;
  const balanceAmount = saleSelectors.balanceSelector(sale);
  const transactionType = saleSelectors.typeSelector(sale);
  const amount = saleSelectors.amountSelector(sale);

  // Check if batch payments is enabled and determine when to skip validation:
  // - Skip validation ONLY for: multiple invoices OR single non-default invoice (when amount > 0)
  // - Apply validation for: no invoices (amount = 0) OR only default invoice selected
  const selectedBatchInvoices = insight ? insightSelectors.selectedBatchInvoices(insight) : [];
  const onlyDefaultInvoiceSelected =
    selectedBatchInvoices?.length === 1 && selectedBatchInvoices[0]?.defaultInvoice;
  const isBatchPayments = isBatchPaymentsEnabled(state) && !onlyDefaultInvoiceSelected;

  closeAllPayPalButtons(paymentMethodType, newPaymentMethodType);
  dispatch(
    paymentActions.paymentMethodTypeChange({
      paymentMethodType,
      newPaymentMethodType,
      balanceAmount,
      paymentMethodSubType: newPaymentMethodSubType,
      region,
      featureFlags,
      transactionType,
      amount,
      amountConfig,
      isBatchPayments,
    })
  );
  dispatch(setDefaultInputAmount());
  if (
    isDateScheduled &&
    (['paypal_ppaam', 'venmo'] as Array<Payment['paymentMethodType']>).includes(
      newPaymentMethodType
    )
  ) {
    dispatch(
      paymentActions.setScheduledDate({
        scheduledDate: new Date(Date.now()),
        isDateScheduled: false,
      })
    );
  }
});

export const initializeApplePay = thunkActionFactory(async ({ dispatch, state }) => {
  let token: string,
    initPayErrorTemplate: {
      sessionId: string;
      token: string;
      userAgent: string;
    };
  try {
    const {
      wallet,
      payment,
      i18n,
      config: { ssrtid, applePay, amountConfig },
      sale,
      insight,
      companyInfo: { region },
      featureFlags,
    } = state;

    // We don't allow ap on subscription invoices or forced autopay invoices
    if (saleSelectors.isSubscription(sale) || isForcedAutopayRecurringSelector(state)) {
      dispatch(walletActions.detectApplePayEnabledStatusFinished());
      return;
    }
    initPayErrorTemplate = {
      sessionId: ssrtid,
      token,
      userAgent: window?.navigator?.userAgent,
    };

    // Token is assigned but never used. Should we remove it?
    token = insightSelectors.tokenSelector(insight);

    // Get values needed for auto-selection on iOS
    const balanceAmount = saleSelectors.balanceSelector(sale);
    const transactionType = saleSelectors.typeSelector(sale);
    const amount = saleSelectors.amountSelector(sale);

    if (wallet && payment) {
      const { enabledPaymentMethods } = wallet;
      const locale = i18n.locale;
      const applePayConfig = applePay;
      const { paymentStatus } = payment;
      const applePayPromiseStart = Date.now();
      let isApplePayEnabledPromise: Promise<boolean>;
      const userAgent = state.auth.userAgent || window?.navigator?.userAgent;

      isApplePayEnabledPromise = isApplePayEnabledBL({
        enabledPaymentMethods,
        locale,
        applePayConfig,
        userAgent,
      });

      isApplePayEnabledPromise = promiseTimeout(
        applePayConfig.timeout || 10000,
        isApplePayEnabledPromise
      );

      try {
        if (await isApplePayEnabledPromise) {
          dispatch({
            type: walletActions.addApplePayEnabledPaymentMethod.type,
          });

          // Auto-select Apple Pay for iOS mobile devices (natural payment expectation)
          const isIOSMobile = isMobile(userAgent) && isAppleMobileProduct(userAgent);
          if (isIOSMobile && paymentStatus !== 'success') {
            dispatch(
              paymentActions.paymentMethodTypeChange({
                newPaymentMethodType: 'ap',
                balanceAmount,
                region,
                featureFlags,
                transactionType,
                amount,
                amountConfig,
              })
            );
          }

          const applePayPromiseRum = Date.now() - applePayPromiseStart;
          splunkReporter.contextual({
            ...initPayErrorTemplate,
            event: 'pay',
            action: 'initializeApplePay',
            logInfo: { logLevel: 'info', logger },
            applePayPromiseRum,
          });
        }
      } catch (e) {
        const applePayPromiseRum = Date.now() - applePayPromiseStart;
        splunkReporter.contextual({
          ...initPayErrorTemplate,
          event: 'pay',
          action: 'initializeApplePay',
          logInfo: { logLevel: 'info', logger },
          error: {
            message: e.message,
            stack: e.stack,
          },
          applePayPromiseRum,
        });
      }

      dispatch(walletActions.detectApplePayEnabledStatusFinished());
    }
  } catch (e) {
    dispatch(walletActions.detectApplePayEnabledStatusFinished());
    splunkReporter.contextual({
      event: 'pay',
      action: 'initializeApplePay',
      ...initPayErrorTemplate,
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: (e as ErrorWithResponse).message,
        stack: e.stack,
      },
      intuit_tid: (e as ErrorWithResponse).config
        ? (e as ErrorWithResponse).config?.headers['intuit_tid']
        : null,
    });
  }
});

export const initializePayPal = thunkActionFactory(({ dispatch, state }) => {
  try {
    const { payment, insight, sale, companyInfo, featureFlags, config } = state;
    const amountConfig = config?.amountConfig;

    if (payment && sale && companyInfo && insight) {
      const { isPayPalCommerceInvoice } = payment;

      const {
        receivable: { balance: balanceAmount },
      } = sale;

      const allowedPaymentMethods = ['paypal'] as Wallet['enabledPaymentMethods'];
      const newPaymentMethodType = 'pp';
      if (isPayPalCommerceInvoice && typeof window !== 'undefined') {
        if (
          !window.paypal ||
          !window.paypal.HostedFields ||
          window.paypal.HostedFields.isEligible() !== true
        ) {
          dispatch(walletActions.changeEnabledPaymentMethods({ allowedPaymentMethods }));
          dispatch(
            paymentActions.paymentMethodTypeChange({
              newPaymentMethodType,
              balanceAmount,
              featureFlags,
              amountConfig,
            })
          );
        }

        dispatch(
          paymentActions.setPayPalCommerceIdempotentKeys({
            ppCreateOrderKey: getUUID(),
            ppCaptureOrderKey: getUUID(),
          })
        );

        dispatch({
          type: walletActions.detectPayPalEnabledStatusFinished.type,
        });
      }
    }
  } catch (e) {
    splunkReporter.contextual({
      sessionId: state.config.ssrtid,
      event: 'pay',
      action: 'initializePayPal',
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: (e as ErrorWithResponse).message,
        stack: e.stack,
      },
      intuit_tid: (e as ErrorWithResponse).config
        ? (e as ErrorWithResponse).config?.headers['intuit_tid']
        : null,
    });
  }
});

export const initializePPAAMPayLater = () => async (dispatch, getState) => {
  const { wallet, payment } = getState();
  const shouldNotShowPPAAMPayLater =
    (wallet && !Array.isArray(wallet.enabledPaymentMethods)) ||
    !payment ||
    (wallet && !wallet.enabledPaymentMethods.includes('paypal_ppaam'));
  if (shouldNotShowPPAAMPayLater) {
    return;
  }

  dispatch(walletActions.updateIsPPAAMInstallmentsEnabled());
  dispatch(
    walletActions.changeEnabledPaymentMethods({
      allowedPaymentMethods: [...(wallet?.enabledPaymentMethods || []), 'paypal_ppaam paylater'],
    })
  );
  dispatch(walletActions.mergeCCAndDCIfMoreThen6PMs());
};

export const initiateBatchPayment = thunkActionFactory(
  async ({ payload = {}, dispatch, state, businessLogic }) => {
    let { ccDetails, bankDetails } = payload;
    const {
      insight,
      sale,
      payment,
      companyInfo,
      wallet: { selectedWalletId, userWallets = [], cardOnFile },
      auth,
      config,
    } = state;

    try {
      // Get SELECTED invoices from Redux (user's selection in BatchInvoiceListTable)
      const selectedInvoices = insightSelectors.selectedBatchInvoices(insight);

      if (!selectedInvoices || selectedInvoices.length === 0) {
        batchPaymentLogs.logInitiateBatchPaymentNoInvoices(logger);
        return;
      }

      // IMPORTANT: Tokenize payment method if it's a new payment method and user wants to save it
      // This is required to create wallet entries, just like regular payment flow
      const isNewPaymentMethod =
        selectedWalletId === null ||
        selectedWalletId === 'AddCard' ||
        selectedWalletId === 'AddBank';
      const shouldSavePaymentMethod = payment.isSavePaymentMethodChecked && auth.isUserSignedIn;

      if (isNewPaymentMethod && shouldSavePaymentMethod) {
        try {
          if (ccDetails) {
            // Tokenize credit card
            const result = await businessLogic.wallet.createShortLivedToken(state, {
              ccDetails,
              paymentMethodType: payment.paymentMethodType,
            });
            if (result && result.token) {
              ccDetails = { ...ccDetails, token: result.token };
            }
          } else if (bankDetails) {
            // Tokenize bank account
            const result = await businessLogic.wallet.createShortLivedToken(state, {
              bankDetails,
              paymentMethodType: payment.paymentMethodType,
            });
            if (result && result.token) {
              bankDetails = { ...bankDetails, token: result.token };
            }
          }
        } catch (tokenizationError: any) {
          batchPaymentLogs.logTokenizationError(logger, tokenizationError);
          // If tokenization fails, show error and stop payment (same as regular payment)
          dispatch(
            paymentActions.createPaymentError({
              error: tokenizationError,
              featureFlags: state.featureFlags,
            })
          );
          return;
        }
      }

      const currency = saleSelectors.currencySelector(sale);
      const riskProfileToken = insightSelectors.riskProfileTokenSelector(insight);
      const saleType = saleSelectors.typeSelector(sale);
      const contactName = saleSelectors.customerNameSelector(sale) || companyInfo?.name || '';
      const contactEmail =
        sale?.contact?.toEmails?.[0] || state?.payment?.contactDetailsEmail || '';
      const payorAuthId = auth?.entityId || '';
      const bioCatchSessionId = config?.bioCatchSessionId;

      // Get payment method details
      const { paymentMethodType } = payment;

      // Get wallet info for payment details (if using saved wallet)
      let selectedWallet;
      if (selectedWalletId && selectedWalletId !== 'AddCard' && selectedWalletId !== 'AddBank') {
        selectedWallet = userWallets.find((w: any) => w.id === selectedWalletId);
      }

      // Prepare batch sales info from SELECTED invoices only
      const batchSalesInfo = createBatchSalesInfo(selectedInvoices, currency || 'USD');
      const totalAmount = calculateTotalAmount(batchSalesInfo);
      // Calculate sum of all original invoice totals for risk/BioCatch API
      const totalOriginalAmount = calculateTotalOriginalAmount(selectedInvoices);

      // Prepare payment method specific info
      let creditCardInfo;
      let achInfo;

      if (ccDetails) {
        // New credit card payment
        creditCardInfo = {
          tokenNumber: ccDetails.token || '',
          payorName: ccDetails.name || contactName,
          cardType: ccDetails.cardType || '',
          zipCode: extractZipCode(ccDetails),
        };
      } else if (bankDetails) {
        // New bank/ACH payment
        achInfo = {
          tokenNumber: bankDetails.token || '',
          bankCode: bankDetails.bankCode || '',
          accountType: bankDetails.accountType || 'CHECKING',
          payorName: bankDetails.name || contactName,
          phoneNumber: bankDetails.phone || '',
        };
      } else if (selectedWallet) {
        // Saved wallet payment (could be card or bank)
        const isCardWallet = selectedWallet.cardNumber || selectedWallet.cardType;

        if (isCardWallet) {
          creditCardInfo = {
            tokenNumber: selectedWallet.walletToken || selectedWallet.cardNumber || '',
            payorName: selectedWallet.payorFirstName || selectedWallet.name || contactName,
            cardType: selectedWallet.cardType || selectedWallet.paymentMethod || '',
            zipCode: extractZipCode(selectedWallet),
          };
        } else {
          // Bank wallet
          achInfo = {
            tokenNumber: selectedWallet.walletToken || selectedWallet.accountNumber || '',
            bankCode: selectedWallet.routingNumber || selectedWallet.bankCode || '',
            accountType: selectedWallet.accountType || 'CHECKING',
            payorName: selectedWallet.payorFirstName || selectedWallet.name || contactName,
            phoneNumber: selectedWallet.phoneNumber || selectedWallet.phone || '',
          };
        }
      } else {
        throw new Error('No valid payment method found for batch payment');
      }

      // Prepare masked payment display
      let maskedPayment = '';
      if (ccDetails) {
        // New card
        maskedPayment = formatMaskedAccountNumber(ccDetails.number || '');
      } else if (bankDetails) {
        // New bank account
        maskedPayment = formatMaskedAccountNumber(bankDetails.accountNumber || '');
      } else if (selectedWallet) {
        // Saved wallet
        maskedPayment =
          selectedWallet.displayLabel ||
          selectedWallet.cardNumber ||
          selectedWallet.accountNumber ||
          '';
      }

      // Determine the correct payment method
      let normalizedPaymentMethod = paymentMethodType || 'credit card';
      if (selectedWallet && selectedWalletId) {
        // Saved wallet payment
        const isCardWallet = selectedWallet.cardNumber || selectedWallet.cardType;
        normalizedPaymentMethod = isCardWallet ? 'credit card wallet' : 'bank wallet';
      } else if (ccDetails) {
        // New credit/debit card
        normalizedPaymentMethod = 'credit card';
      } else if (bankDetails) {
        // New bank account
        normalizedPaymentMethod = 'bank';
      }

      // Prepare batch payment basic info
      const saveToMerchantWallet = cardOnFile?.isCheckboxChecked ? true : undefined;
      const batchPaymentBasicInfo: any = {
        saleType: saleType || 'INVOICE',
        paymentMethod: normalizedPaymentMethod,
        paymentProcessor: 'IMS',
        customerName: contactName,
        currency: currency || 'USD',
        riskProfileToken: riskProfileToken || '',
        maskedPayment:
          maskedPayment || (creditCardInfo ? `${creditCardInfo.cardType}****` : '****'),
        savePaymentMethod: payment.isSavePaymentMethodChecked || false,
        saveToMerchantWallet,
      };

      // Log card on file checkbox state
      batchPaymentLogs.logCardOnFileCheckboxState(logger, {
        isSavePaymentMethodChecked: payment.isSavePaymentMethodChecked || false,
        isNewPaymentMethod:
          selectedWalletId === null ||
          selectedWalletId === 'AddCard' ||
          selectedWalletId === 'AddBank',
      });

      // Add payorAuthId only if it exists
      if (payorAuthId) {
        batchPaymentBasicInfo.payorAuthId = payorAuthId;
      }

      // Add bioCatchSessionId if available
      if (bioCatchSessionId) {
        batchPaymentBasicInfo.bioCatchSessionId = bioCatchSessionId;
      }

      // Prepare contact info
      const contactInfo = {
        name: contactName,
        emails: contactEmail ? [contactEmail] : [],
      };

      // Build batch payment request with payment method specific info
      const batchPaymentRequest = {
        batchPaymentBasicInfo,
        batchSalesInfo,
        contactInfo,
      };

      // Add payment method specific info
      if (creditCardInfo) {
        batchPaymentRequest.creditCardInfo = creditCardInfo;
      }
      if (achInfo) {
        batchPaymentRequest.achInfo = achInfo;
      }

      // Add walletInfo if using saved payment method
      if (selectedWallet && selectedWalletId) {
        batchPaymentRequest.walletInfo = {
          id: String(selectedWalletId),
        };
      }

      // Add notification data and customerId for merchant wallet save
      if (saveToMerchantWallet) {
        batchPaymentRequest.notificationData = {
          payorEmail: contactEmail || '',
          merchantEmail: companyInfoSelectors.emailSelector(companyInfo) || '',
          merchantName: companyInfoSelectors.nameSelector(companyInfo) || '',
        };
        // Add customerId for merchant wallet creation (needed for guest users without C2 wallet)
        const customerId = saleSelectors.customerLocalNameIdSelector(sale);
        if (customerId) {
          batchPaymentRequest.customerId = customerId;
        }
      }

      // Mark payment as in progress
      dispatch(paymentActions.createPayment());
      // Reset merchant wallet confirmation flags when starting a new payment
      dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());

      // Cancel any pending wallet operations before starting batch payment
      dispatch(walletActions.deleteWalletCancel());
      dispatch(walletActions.updateWalletCancel());
      dispatch(modalActions.hide());

      const { featureFlags = {} } = state;
      const ssrtid = config?.ssrtid || '';
      const token = insightSelectors.tokenSelector(insight);

      try {
        await lockForriskProfileToken(() => state);
        const updatedRiskProfileToken = insightSelectors.riskProfileTokenSelector(insight);
        if (updatedRiskProfileToken) {
          batchPaymentRequest.batchPaymentBasicInfo.riskProfileToken = updatedRiskProfileToken;
        }
      } catch (lockError) {
        mark('failed to get riskProfileToken in time for batch payment');
        logIfMissingriskProfileToken({
          ssrtid,
          token,
          riskProfileToken: batchPaymentRequest.batchPaymentBasicInfo.riskProfileToken,
          where: 'batchPayment_afterLockForriskProfileToken',
          featureFlags,
        });
      }

      // Call BioCatch risk prepayment API with batch-specific amounts
      await reportPrePayment(
        {
          // activityAmountTotal: sum of all original invoice totals (for BioCatch fraud detection)
          activityAmountTotal: totalOriginalAmount,
          // activityAmount: total amount being paid in this batch
          activityAmount: parseFloat(totalAmount),
          // activityAmountBalance: total balance being paid (same as amount for batch full balance payments)
          activityAmountBalance: parseFloat(totalAmount),
          paymentMethodType: normalizedPaymentMethod,
        },
        state
      );

      // Import payBatch from sale slice dynamically to avoid circular dependency
      const { payBatch } = await import('store/sale/slice');
      // Call batch payment API
      const batchResponse = await dispatch(payBatch(batchPaymentRequest));
      batchPaymentLogs.logInitiateBatchPaymentSuccess(logger, {
        batchId: batchResponse?.batchId,
      });

      // Handle successful batch payment
      if (batchResponse && batchResponse.batchId) {
        // If the payment method was saved, re-fetch the wallet
        if (batchResponse.savePaymentMethodSuccessful) {
          dispatch(fetchWallets());
        }

        // Store batchId, totalInvoices, and selectedInvoices for batch processing screen
        // Also pass merchantWalletSaveData for deferred COF save (after batch completes with at least one success)
        const selectedInvoices = insight ? insightSelectors.selectedBatchInvoices(insight) : [];
        // Also get ALL batch unpaid invoices for reference (to get docNumber, dueDate, etc.)
        const allBatchInvoices = insight ? insightSelectors.batchUnpaidInvoices(insight) : null;

        const { saleActions: saleActionsImport } = await import('store/sale/slice');
        // Clear any previous full failure error so the banner doesn't persist on retry
        dispatch(saleActionsImport.setBatchFullFailureError(false));
        dispatch(
          saleActionsImport.setBatchProcessing({
            batchId: batchResponse.batchId,
            totalInvoices: selectedInvoices.length,
            selectedInvoices: selectedInvoices, // The invoices that were sent for payment
            allBatchInvoices: allBatchInvoices, // All unpaid invoices for reference data
            // Store data needed for deferred COF save (will be triggered after batch completes with at least one success)
            merchantWalletSaveData: batchResponse.merchantWalletSaveData,
            creditCardInfo: creditCardInfo,
            achInfo: achInfo,
          })
        );

        // Close any open modals (like wallet deletion) when entering batch processing
        dispatch(modalActions.hide());

        // Format success payload similar to regular payment
        const successResult = createBatchPaymentSuccessPayload({
          batchResponse,
          totalAmount,
          maskedPayment,
          cardType: creditCardInfo ? creditCardInfo.cardType : achInfo ? 'ACH' : '',
          ssrtid: state.config?.ssrtid || '',
        });

        dispatch(paymentActions.createPaymentSuccess(successResult));
      } else {
        throw new Error('Invalid batch payment response');
      }
    } catch (error: any) {
      batchPaymentLogs.logBatchPaymentError(logger, error);

      // Handle failed batch payment
      const { featureFlags } = state;
      dispatch(paymentActions.cancelPayment());
      dispatch(
        onFailedPayment(
          {
            response: {
              data: {
                message: error?.response?.data?.message || 'GENERAL_PAYMENT_ERROR',
              },
            },
          },
          featureFlags
        )
      );
    }
  }
);

export const onPayButtonClicked = thunkActionFactory(({ dispatch, state }) => {
  const {
    payment: { paymentMethodType, paymentStatus },
    wallet,
    sale,
    payment,
    featureFlags,
    ixp,
    auth: { isUserSignedIn },
  } = state;
  // Reject payment when...
  const { fetchWalletStatus } = wallet;

  const isFetchingWallets = fetchWalletStatus === WALLET_MAP.FETCH_STATUS.FETCHING;
  const isPaymentInProgress = paymentStatus === TXN_MAP.STATUS.IN_PROGRESS;

  const isSubscription = saleSelectors.isSubscription(sale);
  const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);
  const subscriptionInvoiceNotConsented = isSubscription && !payment.isSavePaymentMethodChecked;

  if (isForcedAutopayRecurring) {
    wavefrontReporter.trackForcedAutopayButtonClicked();
  }

  const isOrphanedInvoice = isOrphanedInvoiceSelector(state);
  if (isOrphanedInvoice) {
    wavefrontReporter.trackOrphanedInvoicePayButtonClicked();
  }

  const isFrictionlessDataInvalid = isFrictionlessCheckoutDataInvalidSelector(state);

  if (isFrictionlessDataInvalid) {
    dispatch(paymentActions.rejectPaymentRequest(undefined));
    dispatch(authActions.setFrictionlessCheckoutDetailsRequired({ isDetailsRequired: true }));
    return;
  }

  if (isPaymentInProgress || (isFetchingWallets && !payment.isResumingPayment)) {
    dispatch(paymentActions.rejectPaymentRequest(undefined));
  }

  if (subscriptionInvoiceNotConsented) {
    dispatch(paymentActions.rejectPaymentRequest(PAYMENT_MAP.REJECT_TYPES.MISSING_CONSENT));
    return;
  }

  if (
    !isUserSignedIn &&
    (isFrictionlessPhase2SubscriptionsEnabled({ featureFlags, ixp }) || isForcedAutopayRecurring)
  ) {
    const isFrictionlessCheckoutEmailValid = isFrictionlessCheckoutEmailValidSelector(state);
    const isFrictionlessCheckoutPhoneValid = isFrictionlessCheckoutPhoneValidSelector(state);
    if (!isFrictionlessCheckoutEmailValid || !isFrictionlessCheckoutPhoneValid) {
      dispatch(authActions.setFrictionlessCheckoutDetailsRequired({ isDetailsRequired: true }));
      return;
    }
  }

  mark('pay button clicked');
  // dispatch({
  //   type: actionTypes.ON_PAY_BUTTON_CLICKED,
  // });

  if (isBankAccountPaymentMethod(paymentMethodType)) {
    const { selectedWalletId } = wallet;

    // Is the user on the ACHForm?
    if (selectedWalletId === 'AddBank' || selectedWalletId === null) {
      // Remotely trigger submission lifecycle of the Wallet Widget form.
      // This flow should eventually call 'walletTokenizationPaymentCreation' when it's valid.
      const remoteSubmissionFn =
        wallet[REMOTE_BUTTON_ACCESS_FUNCTIONALITY.BANK_CREATE_PAYMENT_TRIGGER];
      remoteSubmissionFn();
    }
    // If the user is not on ACHForm, trigger walletTokenizationPaymentCreation directly.
    else {
      dispatch(walletTokenizationPaymentCreation(undefined));
    }
  } else if (isCreditCardPaymentMethod(paymentMethodType)) {
    // Remotely trigger submission lifecycle of the Wallet Widget form.
    // This flow should eventually call 'walletTokenizationPaymentCreation' when it's valid.

    // If it's the CardForm, then the widget form will take over.
    // If it's a card wallet, we require users to input their CVV again - trigger it here!
    const remoteSubmissionFn =
      wallet[REMOTE_BUTTON_ACCESS_FUNCTIONALITY.CARD_CREATE_PAYMENT_TRIGGER];
    remoteSubmissionFn();
    // NOTE: Forms dispatch 'walletTokenizationPaymentCreation' actions on their own. We don't do it here.
  } else if (isNanopayPaymentMethod(paymentMethodType)) {
    dispatch(paymentActions.createPayment());
    // Reset merchant wallet confirmation flags when starting a new payment
    dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());
    dispatch(submitPayment(undefined));
    return;
  } else if (isPayPalPaymentMethod(paymentMethodType)) {
    // Reset merchant wallet confirmation flags when starting a new payment
    dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());
    dispatch(submitPayment(undefined));
  } else {
    dispatch(walletTokenizationPaymentCreation(undefined));
  }
});

export const onContinuePbbButtonClick = thunkActionFactory(
  async ({ dispatch, state, businessLogic }) => {
    const { sale, featureFlags } = state;
    const isValidEmail = isMAIPasPDF({ sale })
      ? businessLogic.payment.isContactDetailsEmailValid
      : true;
    if (!isValidEmail) {
      dispatch(validateContactDetailsEmail());
      return;
    }
    SegmentIO.transactionEngaged({
      scope: 'counterpart_portal',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'bank_continue',
      ui_action: 'clicked',
      ui_object_detail: 'button',
      ui_access_point: 'transaction_flow',
    });
    await reportPrePayment({}, state);
    dispatch(paymentActions.createPayment());
    const status = await businessLogic.payment.handlePbbOrder(state);
    if (status) {
      SegmentIO.transactionFailed();
      const error = { data: { message: 'GENERAL_PAYMENT_ERROR' } };
      if (businessLogic) {
        reportPaymentFailureToPrometheus(businessLogic, error.data.message, 'bank');
      }
      dispatch(onFailedPayment(error, featureFlags));
    }
  }
);

export const onAffirmBnplButtonClick = thunkActionFactory(async ({ dispatch, state }) => {
  const { featureFlags, sale, payment } = state;

  // MAIP-as-PDF + one-to-many: same early-validation as `submitPayment` so errors show before opening Affirm.
  if (
    (isMAIPasPDF({ sale, featureFlags }) && !payment?.contactDetailsEmail?.trim?.()) ||
    (saleSelectors.isOneToManyPaymentRequest(sale) &&
      (!payment?.contactDetailsName?.trim?.() || !payment?.contactDetailsEmail?.trim?.()))
  ) {
    dispatch(validateContactDetailsName());
    dispatch(validateContactDetailsEmail());
    dispatch(paymentActions.cancelPayment());
    return;
  }

  dispatch(paymentActions.createPayment());
  const checkoutObject = (await dispatch(createAffirmBnplOrderThunk())) as any;
  const affirmBnplPublickey = state?.config?.affirm?.affirmBnplPublickey;
  if (checkoutObject?.affirmBNPLCheckoutObject?.merchant) {
    checkoutObject.affirmBNPLCheckoutObject.merchant.public_api_key = affirmBnplPublickey;
  }

  SegmentIO.transactionEngaged({
    activity_type: 'affirm_bnpl_checkout_started',
    ui_action: 'clicked',
    ui_object: 'button',
    ui_object_detail: 'affirm_bnpl_pay_button',
    ui_access_point: 'transaction_flow',
  });
  const affirm = (window as any)?.affirm;
  affirm?.ui?.ready(() => {
    affirm.checkout(checkoutObject?.affirmBNPLCheckoutObject);
    affirm.checkout.open({
      onFail: function (error: any) {
        const isCanceled = error?.reason === 'canceled';
        splunkReporter.contextual({
          logInfo: { logLevel: isCanceled ? 'info' : 'error', logger },
          event: 'pay',
          action: 'affirmBnplCheckoutFailed',
          activityInfo: {
            status: isCanceled ? 'canceled' : 'failed',
            errorReason: error?.reason,
            errorCode: error?.code,
            errorType: error?.type,
          },
          error: isCanceled ? undefined : error,
        });
        if (!isCanceled) {
          dispatch(
            onFailedPayment(
              {
                response: {
                  data: { message: PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR },
                },
              },
              featureFlags
            )
          );
        } else {
          dispatch(paymentActions.cancelPayment());
          SegmentIO.transactionEngaged({
            activity_type: 'affirm_bnpl_canceled',
            ui_action: 'viewed',
            ui_object: 'modal',
            ui_object_detail: 'affirm_bnpl_modal',
            ui_access_point: 'transaction_flow',
          });
        }
      },
      onSuccess: function (data: any) {
        dispatch(confirmAffirmBnplOrderThunk({ checkoutId: data?.checkout_token || '' }) as any);
        splunkReporter.contextual({
          logInfo: { logLevel: 'info', logger },
          event: 'pay',
          action: 'affirmBnplCheckoutSuccess',
          activityInfo: { data },
        });
      },
      onValidationError: function (error: any) {
        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'pay',
          action: 'affirmBnplCheckoutValidationError',
          error,
        });

        dispatch(
          onFailedPayment(
            {
              response: {
                data: { message: PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR },
              },
            },
            featureFlags
          )
        );
      },
    });
  });
});

export const submitPayment = thunkActionFactory<any>(
  async ({ payload = {}, dispatch, state, businessLogic }) => {
    const { bankDetails, ccDetails } = payload;
    let domainId: string,
      token: string,
      currency: string,
      riskProfileToken: string,
      isSubscription: boolean,
      isGpu: boolean | null;
    const {
      payment: {
        paymentMethodType,
        paymentMethodSubType,
        isDateScheduled,
        isAutoPayOn,
        gratuityValue = 0,
        amount,
        isSubscriptionUpdatePaymentMethodRequired,
        inputAmount,
        isSavePaymentMethodChecked,
      },
      auth: { isQBDTInvoiceRollBack, recipientEmail },
      sale,
      insight,
      companyInfo,
      config: { ssrtid },
      wallet: { selectedWalletId, userWallets = [], enabledCCTypes, isNewWalletUI, cardOnFile },
      ixp,
      featureFlags = {},
    } = state;

    domainId = insightSelectors.domainId(insight);
    token = insightSelectors.tokenSelector(insight);
    currency = saleSelectors.currencySelector(sale);
    riskProfileToken = insightSelectors.riskProfileTokenSelector(insight);
    isSubscription = saleSelectors.isSubscription(sale);
    const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);
    isGpu = saleSelectors.isGpu(sale);
    let submitPaymentMark = mark('submitPayment');
    logIfMissingriskProfileToken({
      riskProfileToken,
      featureFlags,
      ssrtid,
      token,
      where: 'submitPaymentStart',
    });
    const object_detail = isSurchargePaintedDoorOnForSale({ sale, featureFlags })
      ? 'surchargeCC'
      : `${SegmentIO.invoice_type}`;

    // Check if this is a batch payment
    const isBatchPaymentEnabled = isBatchPaymentsEnabled(state);
    const selectedBatchInvoices = insight ? insightSelectors.selectedBatchInvoices(insight) : [];
    const isBatchPayment =
      isBatchPaymentEnabled &&
      selectedBatchInvoices.length > 0 &&
      (selectedBatchInvoices.length > 1 ||
        (selectedBatchInvoices.length === 1 && !selectedBatchInvoices[0]?.defaultInvoice));

    // Prepare analytics payload with batch payment info if applicable
    const analyticsPayload: any = { object_detail };
    if (isBatchPayment) {
      analyticsPayload.isBatchPayment = true;
      analyticsPayload.batchInvoicesSelectedCount = selectedBatchInvoices.length;
      analyticsPayload.batchInvoicesSelectedList = selectedBatchInvoices;
    }

    const allowedPaymentMethods = ['bank', 'cc', 'dc'];
    if (cardOnFile && allowedPaymentMethods.includes(paymentMethodType)) {
      const { isCheckboxChecked } = cardOnFile;
      analyticsPayload.is_saved_to_merchant_wallet = isCheckboxChecked;
    }

    const isPayingWithSavedWallet: boolean =
      !!(
        userWallets &&
        userWallets.length &&
        selectedWalletId &&
        selectedWalletId.toString().length > 0 &&
        selectedWalletId !== 'AddCard' &&
        selectedWalletId !== 'AddBank'
      ) || false;

    analyticsPayload.is_paying_with_saved_wallet = isPayingWithSavedWallet;

    SegmentIO.transactionSubmitted(analyticsPayload);

    const isOneToMany = saleSelectors.isOneToManyPaymentRequest(sale);
    const isValidMAIPasPDF = isMAIPasPDF({ sale, featureFlags })
      ? businessLogic.payment.isContactDetailsEmailValid
      : true;
    const validIfOneToMany = isOneToMany
      ? businessLogic.payment.isContactDetailsNameValid &&
        businessLogic.payment.isContactDetailsEmailValid
      : true;
    if (!validIfOneToMany || !isValidMAIPasPDF) {
      dispatch(validateContactDetailsName());
      dispatch(validateContactDetailsEmail());
      dispatch(paymentActions.cancelPayment());
      return;
    }

    // Check for batch payment feature flag (after tokenization completes)
    // Determine if we should use batch payment:
    // - Feature flag/experiment is enabled (subscription check is handled in IXP experiment)
    // - Multiple invoices selected OR single non-default invoice selected
    // Only use batch payment if:
    // 1. More than 1 invoice selected OR
    // 2. Exactly 1 invoice selected but it's NOT the default invoice
    const shouldUseBatchPayment = isBatchPayment;

    if (shouldUseBatchPayment) {
      // Route to batch payment flow with tokenized payment details
      try {
        await dispatch(initiateBatchPayment({ ccDetails, bankDetails }));
        return; // IMPORTANT: Stop here, don't continue with regular payment!
      } catch (batchError) {
        // DON'T continue to regular payment - batch payment already handled the error
        return; // Still return to prevent regular payment
      }
    }

    let paymentBody: {
        isUpdated?: boolean;
        isUpdate?: boolean;
        accountEmail?: string;
        riskProfileToken?: string;
        paidInvoices?: Array<{
          invoiceNumber?: number;
          balance: number;
        }>;
        tokenNumber: string;
      },
      applePayInfo: ApplePayInfo,
      nanopayDetails: {
        nanopayToken: string;
        nanopaySourceAccount: string;
        maskedBankAccountNumber: string;
      };
    if (companyInfo.region === 'GB' && isCreditCardPaymentMethod(paymentMethodType)) {
      const walletInfoPbc: PbcWalletInfo = {
        walletId: selectedWalletId,
        cvc: ccDetails?.cvc,
        walletToken: ccDetails?.token,
      };
      const result = await businessLogic.payment.handlePbcOrder(
        state,
        walletInfoPbc,
        isSavePaymentMethodChecked
      );
      if (!result) {
        return;
      }
      if (result.status === 'error') {
        dispatch(onFailedPayment(result, featureFlags));
        return;
      }
      if (result?.type !== 'immediate') {
        dispatch(paymentActions.createPayment());
        return;
      }
      const paymentStatus = adyenPaymentStatusTranslator(result.paymentResponse?.paymentStatus);
      if (paymentStatus === TXN_MAP.STATUS.ADYEN_STATUSES.APPROVED) {
        dispatch(
          onSuccessfulPayment({
            result: {
              data: result.paymentResponse,
              config: result.config,
            },
            usedPaymentMethod: {
              cardNumber: result?.paymentResponse?.paymentMethod,
              cardType: result?.paymentResponse?.paymentMethod.split('*')[0],
              token: ccDetails?.token,
            },
          })
        );
        return;
      } else {
        const error = { data: { message: 'GENERAL_PAYMENT_ERROR' } };
        dispatch(onFailedPayment(error, featureFlags));
        return;
      }
    }

    if (
      (isDateScheduled || isAutoPayOn) &&
      !isSubscription &&
      (isBankAccountPaymentMethod(paymentMethodType) ||
        isCreditCardPaymentMethod(paymentMethodType))
    ) {
      if (
        isCreditCardPaymentMethod(paymentMethodType) &&
        selectedWalletId &&
        !isAmexAccountEnabled(selectedWalletId, userWallets, enabledCCTypes)
      ) {
        reportPaymentFailureToPrometheus(
          businessLogic,
          'PAYFLOW_VALIDATION_CARD_TYPE_INVALID',
          'american-express'
        );
        dispatch(
          onFailedPayment(
            {
              response: {
                data: {
                  message: 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID',
                  cardType: 'american-express',
                },
              },
            },
            featureFlags
          )
        );
        return;
      }
      paymentBody = schedulePaymentHelpers.getSchedulePaymentPayload({
        state,
        bankDetails,
        ccDetails,
      });
    } else if (isSubscription) {
      paymentBody = subscriptionPaymentHelpers.getSubscriptionPaymentPayload({
        state,
        bankDetails,
        ccDetails,
      });
      if (isSubscriptionUpdatePaymentMethodRequired) {
        SegmentIO.transactionEngaged({
          ui_object: 'button',
          ui_object_detail: 'update_payment_method',
          ui_action: 'clicked',
          ui_access_point: 'transaction_flow',
        });
        paymentBody.isUpdate = true;
      } else {
        SegmentIO.transactionEngaged({
          ui_object: 'button',
          ui_object_detail: 'subscribe',
          ui_action: 'clicked',
          ui_access_point: 'transaction_flow',
        });
      }
      if (ccDetails) {
        paymentBody = {
          ...getCCPaymentPayloadForCpServer(ccDetails, state),
          ...paymentBody,
        };
      }
    } else if (isBankAccountPaymentMethod(paymentMethodType)) {
      paymentBody = getBankPaymentPayloadForCpServer(bankDetails, state);
    } else if (isCreditCardPaymentMethod(paymentMethodType)) {
      if (
        selectedWalletId &&
        !isAmexAccountEnabled(selectedWalletId, userWallets, enabledCCTypes)
      ) {
        reportPaymentFailureToPrometheus(
          businessLogic,
          'PAYFLOW_VALIDATION_CARD_TYPE_INVALID',
          'american-express'
        );
        dispatch(
          onFailedPayment(
            {
              response: {
                data: {
                  message: 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID',
                  cardType: 'american-express',
                },
              },
            },
            featureFlags
          )
        );
        return;
      }
      try {
        paymentBody = getCCPaymentPayloadForCpServer(ccDetails, state);
      } catch (e) {
        reportPaymentFailureToPrometheus(businessLogic, 'CC_PAYMENT_PAYLOAD_INVALID', 'cc');
        dispatch(
          onFailedPayment(
            {
              response: {
                data: { message: 'CC_PAYMENT_PAYLOAD_INVALID', errorMessage: (e as Error).message },
              },
            },
            featureFlags
          )
        );
        return;
      }
    } else if (isNanopayPaymentMethod(paymentMethodType)) {
      const {
        nanopay: { hasNanopayConnected, userBearerToken, payingWithAccount },
      } = state as { nanopay: Nanopay };

      if (!hasNanopayConnected || !userBearerToken || !payingWithAccount || !payingWithAccount.id) {
        reportPaymentFailureToPrometheus(
          businessLogic,
          'NANOPAY_PAYMENT_PAYLOAD_INVALID',
          'nanopay'
        );
        dispatch(
          onFailedPayment(
            { response: { data: { message: 'NANOPAY_PAYMENT_PAYLOAD_INVALID' } } },
            featureFlags
          )
        );
        return;
      }
      try {
        nanopayDetails = {
          nanopaySourceAccount: payingWithAccount.id,
          nanopayToken: userBearerToken,
          maskedBankAccountNumber: formatMaskedAccountNumber(payingWithAccount.accountNumberMask),
        };
        paymentBody = getNanopayBankPaymentPayloadForCpServer(nanopayDetails, state);
      } catch (e) {
        reportPaymentFailureToPrometheus(
          businessLogic,
          'NANOPAY_PAYMENT_PAYLOAD_INVALID',
          'nanopay'
        );
        dispatch(
          onFailedPayment(
            {
              response: {
                data: {
                  message: 'NANOPAY_PAYMENT_PAYLOAD_INVALID',
                  errorMessage: (e as Error).message,
                },
              },
            },
            featureFlags
          )
        );
        return;
      }
    } else if (isApplePayPaymentMethod(paymentMethodType)) {
      let makeApplePayPaymentMark = mark('makeApplePayPayment');
      try {
        const applePayPaymentResult = await makeApplePayPayment(state, businessLogic);
        if (applePayPaymentResult.status === 'CANCELED') {
          dispatch(paymentActions.cancelPayment());
          return;
        }
        applePayInfo = { ...applePayPaymentResult.payment, isGpu };
        paymentBody = getApplePayPaymentPayloadForCpServer(
          applePayPaymentResult.payment.token,
          state
        );
      } catch (e) {
        reportPaymentFailureToPrometheus(businessLogic, e.message, 'applepay');
        splunkReporter.contextual({
          sessionId: ssrtid,
          event: 'pay',
          action: 'submitApplePayPayment',
          logInfo: { logLevel: 'error', logger },
          activityInfo: {
            status: 'error',
          },
          error: {
            message: (e as ErrorWithResponse).message,
            stack: e.stack,
          },
          intuit_tid: (e as ErrorWithResponse).config
            ? (e as ErrorWithResponse).config?.headers['intuit_tid']
            : null,
        });
        dispatch(paymentActions.cancelPayment());
        dispatch(onFailedPayment(e as Error, featureFlags));
        return;
      }
      makeApplePayPaymentMark.finish();
    } else if (isPayPalPaymentMethod(paymentMethodType)) {
      await reportPrePayment({}, state);
      dispatch(makePayPalPayment());
      return;
    } else {
      // TODO: External Payment Method
      throw Error('We do not support external payment methods yet');
    }

    const isFrictionlessSubscriptionPhase2Enabled =
      isSubscription &&
      isFrictionlessPhase2SubscriptionsEnabled({
        featureFlags,
        ixp,
      });

    const isFrictionlessForcedAutopayEnabled = isForcedAutopayRecurring;

    const FL2Email = state?.auth?.username;

    // cp-server pass field named riskProfileToken to PAY API
    paymentBody.accountEmail =
      (isFrictionlessSubscriptionPhase2Enabled || isFrictionlessForcedAutopayEnabled) && !!FL2Email
        ? FL2Email
        : recipientEmail;

    const additionalHeaders: RawAxiosRequestHeaders = {
      Accept: 'application/json, text/javascript, */*; q=0.01',
      'aws-wallet': true,
      token,
    };
    // This field is only for supporting payment flow for QBDT rollback
    // Will need to remove once the transition is complete
    if (isQBDTInvoiceRollBack) {
      additionalHeaders['qbdt-scs-rollback'] = true;
    }

    let result, paymentType: string;

    try {
      await lockForriskProfileToken(() => state)
        .then(() => {
          riskProfileToken = insightSelectors.riskProfileTokenSelector(insight);
          paymentBody.riskProfileToken = riskProfileToken;
        })
        .catch(() => {
          mark('failed to get riskProfileToken in time for payment');
          logIfMissingriskProfileToken({
            ssrtid,
            token,
            riskProfileToken,
            where: 'afterLockForriskProfileToken',
            featureFlags,
          });
        });
      await reportPrePayment(paymentBody, state);
      if (isDateScheduled || (isAutoPayOn && !isSubscription)) {
        let schedulePaymentMark = mark('schedulePayment');
        paymentType = 'schedulePayment';
        result = await businessLogic.payment.schedulePayment({
          domainId,
          token,
          data: paymentBody,
        });

        if (result.status === 200) {
          dispatch(paymentActions.createSchedulePaymentSuccess({ result }));

          // Handle saveToMerchantWalletStatus for scheduled payments
          const saveToMerchantWalletStatus = result.data?.saveToMerchantWalletStatus;
          if (saveToMerchantWalletStatus) {
            if (hasActualErrorContent(saveToMerchantWalletStatus)) {
              dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmationError(true));
              dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmation(false));
            } else if (saveToMerchantWalletStatus === true || saveToMerchantWalletStatus?.error) {
              // Treat as success if it's explicitly true OR if error is empty object (server bug)
              dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmation(true));
              dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmationError(false));
            }
          }
        } else {
          dispatch(paymentActions.createPaymentError(result));
        }

        // Log forced autopay frictionless payment result
        if (isForcedAutopayRecurring) {
          const isFrictionlessCheckoutCompleted = isFrictionlessCheckoutCompletedSelector(state);
          const isFrictionlessUserNameAvailable = isFrictionlessUserNameAvailableSelector(state);

          if (isFrictionlessCheckoutCompleted) {
            const isExistingEmail = !isFrictionlessUserNameAvailable;
            const frictionlessFlow = isExistingEmail ? 'login' : 'signup';
            const isSuccessful = result.status === 200;
            splunkReporter.contextual({
              ssrtid,
              token,
              event: 'forcedAutopay_frictionless_phase2',
              action: isSuccessful ? 'payment_success' : 'payment_failure',
              logInfo: { logLevel: 'info', logger },
              activityInfo: {
                status: isSuccessful ? 'success' : 'failure',
                frictionlessFlow,
                amountPaid: result.data?.amountPaid || amount,
                ...(isSuccessful ? {} : { errorMessage: result.data?.message || 'Unknown error' }),
              },
            });
          }
        }

        schedulePaymentMark.finish();
      } else if (isSubscription) {
        result = await businessLogic.payment.subscriptionPayment({
          domainId,
          token,
          data: paymentBody,
        });
        if (result && result.data.paymentStatus === 'SUCCESS' && result.data.subscriptionInfo) {
          dispatch(updateSubscriptionInfo(result.data.subscriptionInfo));
        }

        // Log subscription frictionless phase 2 payment result
        const isFrictionlessPhase2Enabled = isFrictionlessPhase2SubscriptionsEnabled({
          featureFlags,
          ixp,
        });
        const isFrictionlessUserNameAvailable = isFrictionlessUserNameAvailableSelector(state);
        const isFrictionlessCheckoutCompleted = isFrictionlessCheckoutCompletedSelector(state);

        if (isFrictionlessPhase2Enabled && isFrictionlessCheckoutCompleted) {
          const isExistingEmail = !isFrictionlessUserNameAvailable;
          const frictionlessFlow = isExistingEmail ? 'login' : 'signup';
          const isSuccessful = result.data.paymentStatus === 'SUCCESS';
          splunkReporter.contextual({
            ssrtid,
            token,
            event: 'subscription_frictionless_phase2',
            action: isSuccessful ? 'payment_success' : 'payment_failure',
            logInfo: { logLevel: 'info', logger },
            activityInfo: {
              status: isSuccessful ? 'success' : 'failure',
              frictionlessFlow,
              amountPaid: result.data.amountPaid || amount,
              ...(isSuccessful ? {} : { errorMessage: result.data.message || 'Unknown error' }),
            },
          });
        }
      } else {
        const displayGratuityFeatureBool = displayGratuityFeature(state);
        const frictionlessCheckout = frictionlessCheckoutSelector(state);
        let submitSCSPaymentMark = mark('submitSCSPayment');
        paymentType = 'scsPayment';
        result = await businessLogic.payment.submitSCSPayment({
          ccDetails,
          bankDetails,
          nanopayDetails,
          wallet: state.wallet,
          amountPaid: amount,
          paymentMethodType,
          timeout: 60000,
          applePayInfo,
          ...(displayGratuityFeatureBool &&
            gratuityValue && {
              gratuityAmount: gratuityValue,
              displayGratuityFeature: displayGratuityFeatureBool,
            }),
          featureFlags,
          frictionlessCheckout,
          ixp,
        });
        submitSCSPaymentMark.finish();
      }

      const isSuccessfulPayment =
        result.data.paymentStatus === 'SUCCESS' ||
        result.data.paymentStatus === 'PENDING' ||
        result.data.paymentStatus === 'CAPTURED';

      if (isSuccessfulPayment) {
        // store the paid invoices since it is not part of the payment response currently
        const { paidInvoices } = paymentBody;
        paidInvoices && dispatch(paymentActions.storePaidInvoices({ paidInvoices }));
        // if the payment method was saved re-fetch the wallet
        if (result.data.savePaymentMethodSuccessful) {
          dispatch(fetchWallets());
        }
        const saveToMerchantWalletStatus = result.data?.saveToMerchantWalletStatus;
        if (saveToMerchantWalletStatus) {
          if (hasActualErrorContent(saveToMerchantWalletStatus)) {
            dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmationError(true));
            dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmation(false));
          } else if (saveToMerchantWalletStatus === true || saveToMerchantWalletStatus?.error) {
            // Treat as success if it's explicitly true OR if error is empty object (server bug)
            dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmation(true));
            dispatch(walletActions.setShouldShowSavedMerchantWalletConfirmationError(false));
          }
        }
        let usedPaymentMethod;

        if (bankDetails) {
          usedPaymentMethod = bankDetails;
        } else if (ccDetails) {
          usedPaymentMethod = ccDetails;
        } else if (isApplePayPaymentMethod(paymentMethodType)) {
          // paymentBody.tokenNumber - is a JSON in string (as sent to CP server)
          const {
            token: {
              paymentMethod: { displayName, network },
            },
          } = JSON.parse(paymentBody.tokenNumber);
          usedPaymentMethod = { accountNumber: displayName, cardType: network };
        } else {
          // find the wallet item used for paying
          if (isBankAccountPaymentMethod(paymentMethodType)) {
            usedPaymentMethod = userWallets.find((bank) => bank.id === selectedWalletId);
          } else if (isCreditCardPaymentMethod(paymentMethodType)) {
            usedPaymentMethod = userWallets.find((card) => card.id === selectedWalletId);
          } else if (isNanopayPaymentMethod(paymentMethodType)) {
            const {
              nanopay: { payingWithAccount },
            } = state as { nanopay: Nanopay };
            usedPaymentMethod = {
              accountNumber:
                payingWithAccount && payingWithAccount.accountNumberMask
                  ? payingWithAccount.accountNumberMask
                  : '',
            };
          }
        }
        dispatch(onSuccessfulPayment({ result, usedPaymentMethod }));
      } else {
        reportPaymentFailureToPrometheus(businessLogic, result.data.message, paymentMethodType);
        if (result?.response?.data?.errorCode === PAYMENT_ERROR_TYPES.PAYMENT_ERROR_V2) {
          dispatch(
            modalActions.show({
              background: 'inherit',
              component: CreditCardSurchargingModal,
              componentProps: {
                onContinue: () => window.location.reload(),
                hide: () => window.location.reload(),
              },
            })
          );
          return dispatch(paymentActions.cancelPayment());
        }
        dispatch(onFailedPayment(result, featureFlags));
      }
      const { data = {} } = result;
      const intuit_tid =
        result.config && result.config.headers && result.config.headers['intuit_tid'];

      const saveToMerchantWalletStatus = result.data?.saveToMerchantWalletStatus;
      const didSaveMerchantWalletOrError = saveToMerchantWalletStatus
        ? saveToMerchantWalletStatus?.error
          ? 'error'
          : saveToMerchantWalletStatus
        : undefined;

      businessLogic.insight.reportInvoicePayInCP({
        companyInfo,
        insight,
        sale,
        data,
        intuit_tid,
        ssrtid,
        paymentMethodType,
        paymentType,
        gratuityValue,
        isDateScheduled,
        isAutoPayOn,
        isNewWalletUI,
        inputAmount,
        paymentMethodSubType,
        didSaveMerchantWalletOrError,
        payment: state.payment,
        ixp,
      });

      mark('logged Invoice_pay_in_cp');

      logPerformanceMarks({ token, ssrtid, riskProfileToken, intuit_tid });
    } catch (e) {
      reportPaymentFailureToPrometheus(businessLogic, (e as Error).message, 'unknown');
      const headers: RawAxiosRequestHeaders = (e as ErrorWithResponse)?.config?.headers || {};
      const { intuit_tid } = headers;

      reportPaymentNewSyntax({
        currency,
        gratuityValue,
        intuit_tid,
        isAutoPayOn,
        isDateScheduled,
        isNewWalletUI,
        paymentType,
        amountPaid: amount,
        paymentMethod: paymentMethodType,
        paymentMethodSubType,
        paymentStatus: 'error',
        error: { stack: (e as Error)?.stack, message: (e as Error)?.message },
      });
      SegmentIO.transactionFailed();
      dispatch(onFailedPayment(e as Error, featureFlags));

      // For subscription frictionless phase 2: fetch wallets when payment fails
      if (isFrictionlessSubscriptionPhase2Enabled) {
        dispatch(paymentActions.setIsSavePaymentMethodChecked(false));
        // For subscription frictionless phase 2: reset loading states and ensure error persists
        // First, reset wallet fetch status to clear the isFetchingWallets condition
        dispatch(walletActions.fetchWalletError({ error: null }));
        // Store the current error to preserve it
        const currentError = state.payment.paymentError;
        // Reset loading states
        dispatch(paymentActions.setPaymentStatus(null));
        dispatch(paymentActions.setResumingPayment({ isResuming: false }));
        dispatch(paymentActions.setPendingPayment({ isPending: false }));
        // Ensure the error banner persists by calling createPaymentError again
        if (currentError) {
          dispatch(
            paymentActions.createPaymentError({
              error: { data: { message: currentError.message } },
              featureFlags,
            })
          );
        }
      }

      logPerformanceMarks({
        token,
        ssrtid,
        riskProfileToken,
        intuit_tid: (e as ErrorWithResponse).config
          ? (e as ErrorWithResponse).config?.headers['intuit_tid']
          : null,
      });
    }
    submitPaymentMark.finish();
  }
);

export const walletTokenizationPaymentCreation = thunkActionFactory<any>(
  async ({ payload = {}, dispatch, state }) => {
    // For PCI compliance we need to tokenize financial information
    // Tokenization means sending the raw information to the wallet service
    // (they are PCI compliant) and they will give us a token that represents
    // this payment method (or wallet).

    const { bankDetails, ccDetails } = payload as {
      bankDetails: UserWalletBank;
      ccDetails: UserWalletCard;
    };
    let invoiceToken: string;
    const { config, payment, insight, wallet, sale, featureFlags, ixp, auth } = state;

    const { paymentMethodType } = payment;
    if (payment && payment.isPayPalCommerceInvoice) {
      dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());
      return dispatch(paymentActions.createPayment());
    }

    invoiceToken = insightSelectors.tokenSelector(insight);

    if (payment.paymentStatus === TXN_MAP.STATUS.IN_PROGRESS) {
      Logger.debug('Payment is in progress, we need to reject this.');
      return dispatch(paymentActions.rejectPaymentRequest());
    }

    // Check if this is a frictionless subscription phase 2 flow BEFORE any payment processing
    const isSubscription = saleSelectors.isSubscription(sale);
    const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);
    const isFrictionlessPhase2Enabled = isFrictionlessPhase2SubscriptionsEnabled({
      featureFlags,
      ixp,
    });
    const isUserSignedIn = auth.isUserSignedIn;

    const isFrictionlessUserNameAvailable = isFrictionlessUserNameAvailableSelector(state);

    // For forced autopay, consent is implicit - automatically set save payment method flag
    if (isForcedAutopayRecurring && !payment.isSavePaymentMethodChecked) {
      dispatch(paymentActions.setIsSavePaymentMethodChecked(true));
    }

    if (
      ((isSubscription && isFrictionlessPhase2Enabled) || isForcedAutopayRecurring) &&
      !isUserSignedIn &&
      isFrictionlessUserNameAvailable
    ) {
      // Payment method validation passed, now trigger the identity widget (OTP)
      dispatch(paymentActions.createPayment()); // This sets paymentStatus to IN_PROGRESS (shows loader)
      // Reset merchant wallet confirmation flags when starting a new payment
      dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());
      dispatch(paymentActions.setPendingPayment({ isPending: true }));
      dispatch(
        authActions.setFrictionlessRegistrationRequired({
          isRegistrationRequired: true,
        })
      );
      return; // Don't proceed with payment flow - wait for identity widget (OTP) completion
    }

    const isCardCreation = (['cc', 'dc', 'dc,cc'] as Array<Payment['paymentMethodType']>).includes(
      paymentMethodType
    );
    const isNotExternalPayment = !(['pp'] as Array<Payment['paymentMethodType']>).includes(
      paymentMethodType
    );

    if (isCardCreation) {
      const { selectedWalletId } = wallet;
      if (selectedWalletId === null || selectedWalletId === 'AddCard') {
        Logger.debug('CardForm! We will tokenize it here');

        try {
          return await dispatch(walletCardTokenization(ccDetails));
        } catch (e) {
          splunkReporter.contextual({
            ssrtid: config.ssrtid,
            token: invoiceToken,
            event: 'pay',
            action: 'walletTokenization',
            logInfo: { logLevel: 'error', logger },
            activityInfo: {
              status: 'error',
            },
            error: {
              message: (e as ErrorWithResponse).message,
              stack: e?.stack,
            },
          });
          return dispatch(paymentActions.tokenizeWalletError({ error: e }));
        }
      }
    }

    dispatch(paymentActions.createPayment());
    // Reset merchant wallet confirmation flags when starting a new payment
    dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());

    if (isNotExternalPayment) {
      return dispatch(walletBankTokenization(bankDetails));
    }
  }
);

/**
 * Internal thunk that handles payment failure with IXP bucketing.
 * On FIRST failure: sets retry state and buckets user into Payment Retry experiment.
 * On ALL failures: increments failure count, logs to Splunk, and dispatches error action.
 */
const onFailedPaymentThunk = thunkActionFactory<{
  error: ErrorWithResponse;
  featureFlags: FeatureFlags;
}>(async ({ payload, dispatch, state }) => {
  const { error, featureFlags } = payload;
  const { payment } = state;

  mark('failed payment');
  SegmentIO.transactionFailed();

  const isFirstFailure = !payment.isPaymentRetry;

  if (isFirstFailure) {
    // Set retry state on first failure
    dispatch(
      paymentActions.setPaymentRetryState({
        isPaymentRetry: true,
        originalPaymentAmount: payment.amount,
        originalPaymentMethod: payment.paymentMethodType,
      })
    );

    // IXP bucketing - check if experiments were returned from /pay response (server-side bucketing)
    const ixpExperiments = error?.response?.data?.ixpExperiments || error?.data?.ixpExperiments;
    if (ixpExperiments && Object.keys(ixpExperiments).length > 0) {
      dispatch(ixpActions.fetchExperiments({ experiments: ixpExperiments }));

      // Track experiment:mapped event for PaymentRetryExperiment
      const paymentRetryExperiment = ixpExperiments.PaymentRetryExperiment;
      if (paymentRetryExperiment?.experimentId && paymentRetryExperiment?.treatmentKey) {
        SegmentIO.experimentMapped(
          paymentRetryExperiment.experimentId,
          paymentRetryExperiment.treatmentKey
        );
      }
    }
  }

  // Increment failure count on every failure
  dispatch(paymentActions.incrementPaymentFailureCount());

  // Log payment error to Splunk for analytics (gated by feature flag)
  if (featureFlags?.['SBSEG-CP-cp-payment-error-splunk']) {
    try {
      const errorInfo = extractPaymentErrorInfo(error);
      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'pay',
        action: 'paymentError',
        activityInfo: {
          // Error info (predefined enum values only - no raw messages)
          errorCode: errorInfo.errorCode,
          errorType: errorInfo.errorType,
          reasonCode: errorInfo.reasonCode,
          httpStatusCode: errorInfo.httpStatusCode,
          // Payment context
          paymentMethod: payment.paymentMethodType,
          amount: payment.amount,
          // Retry info
          isPaymentRetry: payment.isPaymentRetry,
          paymentFailureCount: (payment.paymentFailureCount || 0) + 1, // +1 because increment hasn't been applied yet
          originalPaymentMethod: payment.originalPaymentMethod,
        },
      });
    } catch (loggingError) {
      // Silently fail - error logging should not impact payment flow
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to log payment error to Splunk:', loggingError);
      }
    }
  }

  // Dispatch the original error action
  dispatch(paymentActions.createPaymentError({ error, featureFlags }));
});

/**
 * Exported wrapper that maintains backward compatibility with existing call sites.
 * All existing `dispatch(onFailedPayment(error, featureFlags))` calls continue to work.
 */
export const onFailedPayment = (error: ErrorWithResponse, featureFlags: FeatureFlags) => {
  return onFailedPaymentThunk({ error, featureFlags });
};

const walletCardTokenization = thunkActionFactory<any>(
  async ({ payload, dispatch, state, businessLogic }) => {
    const ccDetails = payload;
    const { config, wallet, featureFlags, insight, payment, sale, auth } = state;
    const frictionlessCheckout = frictionlessCheckoutSelector(state);
    const { email: frictionlessCheckoutEmail } = frictionlessCheckout || {};
    const payorEmail = getPayorEmail({
      isUserSignedIn: auth?.isUserSignedIn,
      authUsername: auth?.username,
      frictionlessCheckoutEmail,
      payorEmail: sale?.contact?.toEmails?.[0],
    });
    const invoiceToken = insightSelectors.tokenSelector(insight);

    if (wallet.enabledCCTypes.indexOf('amex') === -1 && ccDetails.cardType === 'american-express') {
      reportPaymentFailureToPrometheus(
        businessLogic,
        'PAYFLOW_VALIDATION_CARD_TYPE_INVALID',
        ccDetails.cardType
      );
      dispatch(
        onFailedPayment(
          {
            response: {
              data: { message: 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID', ...ccDetails },
            },
          },
          featureFlags
        )
      );
    }

    const postalCode = getZipCode(ccDetails);
    if (!postalCode) {
      splunkReporter.contextual({
        event: 'pay',
        action: 'walletTokenization',
        logInfo: { logLevel: 'info', logger },
        ssrtid: config.ssrtid,
        token: invoiceToken,
        message: 'missing zip code',
      });
    }
    ccDetails.address = {
      ...ccDetails.address,
      postalCode,
      country: payment.country.a2,
      addressComponents: ccDetails.address?.addressComponents, // Preserve billing address fields
    };
    dispatch(paymentActions.tokenizeWallet());

    let token: string, backUpToken: string;

    // Add payor email to metadata for wallet creation
    if (payorEmail) {
      ccDetails.metadata = [{ name: 'payorEmail', value: payorEmail }];
    }

    try {
      ({ token, backUpToken } = await businessLogic.wallet.createShortLivedToken(state, {
        ccDetails,
      }));
    } catch (e) {
      return walletTokenizationErrorHandler(dispatch, state, e as Error);
    }

    // Add this to the ccDetails
    ccDetails.token = token;
    ccDetails.backUpToken = backUpToken;

    return dispatch(submitPayment({ ccDetails }));
  }
);

const walletBankTokenization = thunkActionFactory<any>(
  async ({ payload, dispatch, state, businessLogic }) => {
    const bankDetails = payload;
    const {
      payment: { paymentMethodType },
      sale,
      auth,
    } = state;
    const frictionlessCheckout = frictionlessCheckoutSelector(state);
    const { email: frictionlessCheckoutEmail } = frictionlessCheckout || {};
    const payorEmail = getPayorEmail({
      isUserSignedIn: auth?.isUserSignedIn,
      authUsername: auth?.username,
      frictionlessCheckoutEmail,
      payorEmail: sale?.contact?.toEmails?.[0],
    });

    if (bankDetails) {
      // Add payor email to metadata for wallet creation
      if (payorEmail) {
        bankDetails.metadata = [{ name: 'payorEmail', value: payorEmail }];
      }

      try {
        const result = await businessLogic.wallet.createShortLivedToken(state, {
          bankDetails,
          paymentMethodType,
        });

        if (result && result.token) {
          bankDetails.token = result.token;
        }
      } catch (e) {
        return walletTokenizationErrorHandler(dispatch, state, e as Error);
      }
    }
    return dispatch(submitPayment({ bankDetails }));
  }
);

const walletTokenizationErrorHandler = (dispatch: Dispatch<any>, state: Payment, e = {}) => {
  const {
    featureFlags,
    config: { ssrtid },
  } = state;
  splunkReporter.contextual({
    sessionId: ssrtid,
    logInfo: { logLevel: 'error', logger },
    event: 'pay',
    action: 'walletTokenization',
    activityInfo: {
      status: 'error',
    },
    error: {
      message: e.message,
      stack: e.stack,
    },
  });
  return dispatch(onFailedPayment(e, featureFlags));
};

export const validateContactDetailsName = thunkActionFactory(
  ({ dispatch, businessLogic, state }) => {
    const displayName = saleSelectors.customerNameSelector(state.sale);
    if (displayName) {
      dispatch(paymentActions.validateContactDetailsName(true));
    } else {
      dispatch(
        paymentActions.validateContactDetailsName(businessLogic.payment.isContactDetailsNameValid)
      );
    }
  }
);

export const validateContactDetailsEmail = thunkActionFactory(
  ({ dispatch, businessLogic, state }) => {
    const customerEmail = saleSelectors.toEmailsSelector(state.sale)[0];
    if (customerEmail) {
      dispatch(paymentActions.validateContactDetailsEmail(true));
    } else {
      dispatch(
        paymentActions.validateContactDetailsEmail(businessLogic.payment.isContactDetailsEmailValid)
      );
    }
  }
);

export const makeApplePayPayment = async (state: RootState) => {
  let companyName: string, companyLocale: string, currency: string;
  const {
    sale,
    config: { applePay, portal, ssrtid },
    payment: { amount },
    wallet: { enabledCCTypes },
    companyInfo,
  } = state;

  currency = saleSelectors.currencySelector(sale);
  companyLocale = companyInfoSelectors.localeSelector(companyInfo);
  companyName = companyInfoSelectors.nameSelector(companyInfo);

  const headers: RawAxiosRequestHeaders = getHeadersForSSRRequest(state);
  const countryCode = convertLocaleToCountry(companyLocale);
  const foundApplePayCountry = applePay.countryConfig.find(
    (countrySettings: { country: string }) => countrySettings.country === countryCode
  );
  let applePaySupportedCardsInLocale: Array<string> | undefined;
  try {
    applePaySupportedCardsInLocale = foundApplePayCountry && foundApplePayCountry.supportedNetworks;
  } catch (e) {
    splunkReporter.contextual({
      sessionId: ssrtid,
      event: 'pay',
      action: 'makeAppPayPayment',
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: `foundApplePayCountry.supportedNetworks - ${(e as Error).message}`,
        stack: (e as Error).stack,
      },
    });
    applePaySupportedCardsInLocale = [];
  }

  const paymentRequest = {
    companyName,
    amount,
    currencyCode: currency,
    countryCode,
    supportedNetworks: enabledCCTypes,
  };

  mark('openApplePayPayment');

  return await openApplePayPaymentBL({
    paymentRequest,
    applePaySupportedCardsInLocale,
    portal,
    headers,
  });
};

const makePayPalPayment = thunkActionFactory(async ({ dispatch, state }) => {
  const { payment, auth, insight, sale, companyInfo, config } = state;

  const domainId = insightSelectors.domainId(insight);
  const token = insightSelectors.tokenSelector(insight);
  const paymentDueStatus = insightSelectors.paymentDueStatusSelector(insight);
  const offerId = insightSelectors.offerIdSelector(insight);
  const balanceAmount = saleSelectors.balanceSelector(sale);
  const invoiceAmount = saleSelectors.amountSelector(sale);
  const transactionType = saleSelectors.typeSelector(sale);
  const currency = saleSelectors.currencySelector(sale);
  const companyLocale = companyInfoSelectors.localeSelector(companyInfo);
  const { isPayPalCommerceInvoice } = payment;

  if (!window.paypal) {
    splunkReporter.contextual({
      sessionId: config.ssrtid,
      event: 'pay',
      action: 'CPV2makePayPalPayment',
      isPayPalCommerceInvoice,
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: 'Object window.paypal is undefined',
      },
    });
    dispatch(paymentActions.cancelPayment());
    return;
  }

  // Open PayPal dialog
  openPayPalSandBox();

  try {
    let getPayPalCheckOutMark = mark('getPayPalCheckOut');
    const payPalCheckOut = await getPayPalCheckOut({
      domainId,
      token,
      balanceAmount,
      paymentDueStatus,
      invoiceAmount,
      transactionType,
      currency,
      companyLocale,
      offerId,
      payment,
      auth,
      config,
    });
    const {
      data: { paypalToken, success },
    } = payPalCheckOut;
    if (success) {
      startPayPalFlow(paypalToken);
    } else {
      closePayPalFlow();
      dispatch(paymentActions.cancelPayment());
    }
    getPayPalCheckOutMark.finish();
  } catch (e) {
    closePayPalFlow();
    dispatch(paymentActions.cancelPayment());
    splunkReporter.contextual({
      sessionId: config.ssrtid,
      event: 'pay',
      action: 'CPV2MakePayPalPayment',
      isPayPalCommerceInvoice,
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: (e as Error).message,
        stack: (e as Error).stack,
      },
    });
    mark('error getPayPalCheckOut');
  }
});

export const createAffirmBnplOrderThunk = thunkActionFactory(
  async ({ dispatch, state, businessLogic }) => {
    try {
      const result = await businessLogic.payment.createAffirmBnplOrder(state);
      return result?.data;
    } catch (e) {
      SegmentIO.transactionViewed({
        activity_type: 'affirm_bnpl_error',
        ui_action: 'viewed',
        ui_object: 'modal',
        ui_object_detail: 'affirm_bnpl_modal',
        ui_access_point: 'transaction_flow',
      });
      const error = { data: { message: 'AFFIRM_BNPL_CREATE_ORDER', error: e } };
      return dispatch(onFailedPayment(error, state.featureFlags));
    }
  }
);

export const confirmAffirmBnplOrderThunk = thunkActionFactory<{
  checkoutId: string;
  email?: string;
}>(async ({ payload, dispatch, state, businessLogic }) => {
  const { checkoutId, email } = payload || ({} as any);
  if (!checkoutId) {
    return;
  }
  try {
    dispatch(paymentActions.setPaymentProcessingMessage('AFFIRM_BNPL_PROCESSING'));
    const result = await businessLogic.payment.confirmAffirmBnplOrder(state, checkoutId, email);

    // success/failure flows
    if (result?.data?.paymentStatus === 'ORDER_CAPTURED') {
      SegmentIO.transactionViewed({
        activity_type: 'affirm_bnpl_approved',
        ui_action: 'viewed',
        ui_object: 'modal',
        ui_object_detail: 'affirm_bnpl_modal',
        ui_access_point: 'transaction_flow',
      });
      dispatch(onSuccessfulPayment({ result, usedPaymentMethod: {} }));
    } else {
      const data = result?.data || { message: 'Affirm BNPL confirm failed' };
      dispatch(onFailedPayment({ data }, state.featureFlags));
    }
  } catch (e) {
    SegmentIO.transactionViewed({
      activity_type: 'affirm_bnpl_error',
      ui_action: 'viewed',
      ui_object: 'modal',
      ui_object_detail: 'affirm_bnpl_modal',
      ui_access_point: 'transaction_flow',
    });
    const error = { data: { message: 'AFFIRM_BNPL_CONFIRM_ORDER', error: e } };
    return dispatch(onFailedPayment(error, state.featureFlags));
  }
});

export const onSuccessfulPayment = thunkActionFactory<{
  result: {
    data: {
      amountPaid: number;
    };
    config: {
      headers: {
        intuit_tid: string;
      };
    };
  };
  usedPaymentMethod: {
    accountNumber?: string;
    cardNumber?: string;
    cardType?: CardType;
    token?: string;
  };
}>(({ payload, dispatch }) => {
  const { result, usedPaymentMethod = {} } = payload;
  const { accountNumber, cardNumber, cardType, token } = usedPaymentMethod;

  // It doesn't matter if it's ACHForm, CardForm, wallet, or wallet that was just saved, we always assign this field.
  if (accountNumber) {
    result.data.maskedAccountNumber = accountNumber;
    if (token) {
      result.data.bankShortLivedToken = token;
    }
  } else if (cardNumber) {
    result.data.maskedCardNumber = cardNumber;
    result.data.cardType = cardType;
    if (token) {
      result.data.cardShortLivedToken = token;
    }
  }

  dispatch(paymentActions.createPaymentSuccess({ result }));

  const {
    data: { amountPaid },
  } = result;

  dispatch(updateNewBalance({ amountPaid }));
  mark('successful payment');
});

// Resume pending payment after identity widget completion
export const resumePendingPayment = thunkActionFactory(({ dispatch, state }) => {
  const { payment } = state;

  // Check if there's a pending payment
  if (payment.isPendingPayment) {
    // Clear the pending state
    dispatch(paymentActions.setPendingPayment({ isPending: false }));

    // Reset the registration required state
    dispatch(
      authActions.setFrictionlessRegistrationRequired({
        isRegistrationRequired: false,
      })
    );

    // Clear any previous payment status to prevent IN_PROGRESS blocking
    dispatch(paymentActions.cancelPayment());

    // Mark as resuming payment to bypass wallet fetching check
    dispatch(paymentActions.setResumingPayment({ isResuming: true }));
    dispatch(onPayButtonClicked());
  }
});

function logPerformanceMarks({
  token,
  ssrtid,
  riskProfileToken,
  intuit_tid,
}: {
  token: string;
  ssrtid: string;
  riskProfileToken: string;
  intuit_tid: string;
}) {
  if (
    window &&
    window.performance &&
    !!window.performance.measure &&
    !!window.performance.getEntriesByType
  ) {
    splunkReporter.contextual({
      event: 'bfs',
      action: 'PerformanceReport',
      logInfo: { logLevel: 'info', logger },
      message: 'Logging performance marks',
      marks: performance.getEntriesByType('mark'),
      durations: performance.getEntriesByType('measure'),
      timeOrigin: performance.timeOrigin ? performance.timeOrigin : 'not supported',
      performance: performance.toJSON ? performance.toJSON() : 'not supported',
      token,
      ssrtid,
      riskProfileToken,
      intuit_tid,
    });
  }
}

export const setScheduledDate = thunkActionFactory<Date>(({ payload, dispatch, state }) => {
  const scheduledDate = payload;
  const { featureFlags } = state;

  const isDateScheduled =
    new Date(scheduledDate) > new Date() ||
    (featureFlags['should-ignore-due-date-schedule-pay'] && scheduledDate);
  SegmentIO.transactionEngaged({
    activity_type: 'schedule_pay',
    ui_object: 'dropdown',
    ui_object_detail: 'select_schedule_pay_date',
    ui_action: 'clicked',
    ui_access_point: 'transaction_flow',
  });
  dispatch(
    paymentActions.setScheduledDate({
      scheduledDate,
      isDateScheduled,
    })
  );
});

export const setSelectedBank = thunkActionFactory<Object>(({ payload, dispatch }) => {
  const selectedBank = payload;
  dispatch(
    paymentActions.setSelectedBank({
      selectedBank,
    })
  );
});

export const hideSuccessScreen = thunkActionFactory(({ dispatch }) => {
  dispatch(setDefaultInputAmount());
  dispatch(paymentAmountReset());
  dispatch(paymentActions.hideSuccessScreen());
  dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());
});

export const createPaypalOrder = thunkActionFactory<Payment['paymentMethodType']>(
  async ({ payload, dispatch, state, businessLogic }) => {
    const usedPaymentMethod = payload;
    const {
      payment: {
        gratuityValue: gratuityAmount,
        amount: paymentAmount,
        contactDetailsName,
        contactDetailsEmail,
      } = {},
      sale,
      featureFlags = {},
    } = state;
    try {
      // Check if MAIP as PDF validation is required
      const isValidMAIPasPDF = isMAIPasPDF({ sale, featureFlags })
        ? businessLogic.payment.isContactDetailsEmailValid
        : true;
      const isOneToManyPaymentRequest = saleSelectors.isOneToManyPaymentRequest(sale);
      if (!isValidMAIPasPDF || isOneToManyPaymentRequest) {
        await dispatch(validateContactDetailsEmail());
        await dispatch(validateContactDetailsName());
        const isEmailValid = businessLogic.payment.isContactDetailsEmailValid;
        const isNameValid = businessLogic.payment.isContactDetailsNameValid;
        if (!isEmailValid || !isNameValid) {
          dispatch(paymentActions.cancelPayment());
          return;
        }
      }
      // Always call reportPrePayment before proceeding
      await reportPrePayment({}, state);

      const response = await businessLogic.payment.handlePaypalOrder({
        action: 'CREATE',
        usedPaymentMethod,
        gratuityAmount,
        paymentAmount,
        intuitOrderId: undefined,
        contactDetailsName,
        contactDetailsEmail,
        featureFlags,
      });
      const { orderId: intuitOrderId, paypalOrderId } = (response && response.data) || {};
      if (!intuitOrderId) {
        const error = { data: { message: 'PAYPAL_CREATE_ORDER' } };
        if (businessLogic) {
          reportPaymentFailureToPrometheus(businessLogic, error.data.message, 'paypal_ppaam');
        }
        return dispatch(onFailedPayment(error, featureFlags));
      }
      dispatch(
        paymentActions.setPayPalOrderId({
          intuitOrderId,
        })
      );
      return paypalOrderId;
    } catch (e) {
      const error = { data: { message: 'PAYPAL_CREATE_ORDER' } };
      if (businessLogic) {
        reportPaymentFailureToPrometheus(businessLogic, error.data.message, 'paypal_ppaam');
      }
      return dispatch(onFailedPayment(error, featureFlags));
    }
  }
);

export const cancelScheduledPayment = thunkActionFactory(
  async ({ dispatch, state, businessLogic }) => {
    const {
      sale: { scheduleInfo, recurringInfo },
      insight: { token, domainId },
    } = state;
    // Use scheduleTemplateId if available, otherwise fallback to recurringId
    // This handles the case when canceling immediately after schedule (on success page)
    // where scheduleTemplateId may not be populated yet
    const scheduleTemplateId =
      (scheduleInfo && scheduleInfo.scheduleTemplateId) ||
      (recurringInfo && recurringInfo.recurringId);

    const result = await businessLogic.payment.deleteSchedulePayment({
      token,
      domainId,
      id: scheduleTemplateId,
    });

    if (result.status === 200) {
      dispatch(cancelScheduledPaymentSuccess());
    } else {
      dispatch(cancelScheduledPaymentError(result));
    }
  }
);

const cancelScheduledPaymentSuccess = thunkActionFactory(async () => {
  location.reload();
});

const cancelScheduledPaymentError = thunkActionFactory<{ error: Error }>(
  async ({ payload, dispatch }) => {
    dispatch(paymentActions.createPaymentError(payload));
  }
);

export const setContactDetailsName = thunkActionFactory<Payment['contactDetailsName']>(
  ({ payload, dispatch, businessLogic }) => {
    businessLogic.payment.contactDetailsName = payload;
    dispatch(paymentActions.setContactDetailsName(businessLogic.payment.contactDetailsName));
  }
);

export const setContactDetailsEmail = thunkActionFactory<Payment['contactDetailsEmail']>(
  ({ dispatch, payload, businessLogic }) => {
    businessLogic.payment.contactDetailsEmail = payload;
    dispatch(paymentActions.setContactDetailsEmail(businessLogic.payment.contactDetailsEmail));
  }
);

export const checkIFPaypalEnabled = thunkActionFactory(({ dispatch, state, businessLogic }) => {
  try {
    const {
      sale: { type },
      wallet: { enabledPaymentMethods, userWallets } = {},
      payment: { paymentMethodType } = {},
      insight: { view2pay: { IsPayable } = {} },
      companyInfo: { sourceOffering } = {},
      featureFlags = {},
    } = state;
    if (
      IsPayable &&
      Array.isArray(enabledPaymentMethods) &&
      enabledPaymentMethods.includes('paypal_ppaam')
    ) {
      const payload = businessLogic.payment.checkIFPaypalEnabled({
        sourceOffering,
        saleType: type,
        enabledPaymentMethods,
        featureFlags,
        paymentMethodType,
        userWallets,
      }) as { paymentMethodType: Payment['paymentMethodType'] };
      dispatch(paymentActions.shouldShowPaypalAndVenmo(payload));
      dispatch(walletActions.shouldShowPaypalAndVenmo(payload));
    }
  } catch (e) {
    splunkReporter.contextual({
      sessionId: state.config.ssrtid,
      event: 'pay',
      action: 'checkIFPaypalEnabled',
      logInfo: { logLevel: 'error', logger },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: (e as Error).message,
        stack: (e as Error).stack,
      },
    });
  }
  dispatch(walletActions.detectPayPalEnabledStatusFinished());
});

export const setIsSavePaymentMethodChecked = thunkActionFactory<
  Payment['isSavePaymentMethodChecked']
>(({ payload, dispatch, businessLogic }) => {
  businessLogic.payment.isSavePaymentMethodChecked = payload;
  SegmentIO.transactionEngaged({
    activity_type: 'wallet',
    ui_object: 'checkbox',
    ui_object_detail: 'save_payment_method',
    ui_action: payload ? 'enabled' : 'disabled',
    ui_access_point: 'transaction_flow',
  });
  dispatch(
    paymentActions.setIsSavePaymentMethodChecked(businessLogic.payment.isSavePaymentMethodChecked)
  );
});

const cancelSubscriptionPaymentError = thunkActionFactory<any>(async ({ payload, dispatch }) => {
  dispatch(paymentActions.createPaymentError({ error: payload }));
});

export const cancelSubscriptionPayment = thunkActionFactory(
  async ({ dispatch, state, businessLogic }) => {
    const {
      insight: { token, domainId },
    } = state;

    const result = await businessLogic.payment.cancelSubscriptionPayment({ token, domainId });

    if (result.status === 200) {
      location.reload();
    } else {
      dispatch(cancelSubscriptionPaymentError(result));
    }
  }
);

export const confirmPaypalOrder = thunkActionFactory(
  async ({ payload, dispatch, state, businessLogic }) => {
    const usedPaymentMethod = payload;
    const {
      companyInfo,
      insight,
      sale,
      ixp,
      featureFlags,
      payment = {},
      config: { ssrtid } = {},
    } = state;
    const { paymentMethodSubType } = payment;
    const handleFailedPayment = ({
      data,
      intuit_tid,
      paymentMethod,
    }: {
      data: {
        gratuityAmount: number;
        intuitOrderId: string;
        intuit_tid: string;
        paymentAmount: number;
        paymentMethod: PaymentMethod | undefined;
        paymentStatus: keyof typeof TXN_MAP.STATUS;
      };
      intuit_tid: string;
      paymentMethod: PaymentMethod | undefined;
    }) => {
      if (businessLogic) {
        reportPaymentFailureToPrometheus(businessLogic, data.message, paymentMethod);
      }
      dispatch(onFailedPayment(data, featureFlags));
      businessLogic.insight.reportInvoicePayInCP({
        companyInfo,
        insight,
        sale,
        data,
        intuit_tid,
        ssrtid,
        paymentMethod,
        paymentMethodSubType,
      });
    };
    try {
      const {
        gratuityValue: gratuityAmount,
        amount: paymentAmount,
        intuitOrderId,
        contactDetailsName,
        contactDetailsEmail,
      } = payment;
      const frictionlessCheckout = frictionlessCheckoutSelector(state);
      const payorEmail = saleSelectors.toEmailsSelector(sale)?.[0];
      const { isSelected = false, email: frictionlessCheckoutEmail = undefined } =
        frictionlessCheckout || {};
      let additionalPayorRecipient = null;
      if (
        isSelected &&
        frictionlessCheckoutEmail &&
        payorEmail &&
        frictionlessCheckoutEmail !== payorEmail
      ) {
        additionalPayorRecipient = frictionlessCheckoutEmail;
      }

      const result = await businessLogic.payment.handlePaypalOrder({
        action: 'CONFIRM',
        intuitOrderId,
        usedPaymentMethod,
        gratuityAmount,
        paymentAmount,
        contactDetailsName,
        contactDetailsEmail,
        featureFlags,
        additionalPayorRecipient,
      });
      const { data = {}, config = {} } = result || {};
      const { paymentStatus, paymentMethod } = data;
      const intuit_tid = config.headers && result.config.headers['intuit_tid'];

      if (
        paymentStatus === 'ORDER_CAPTURED' &&
        ['VENMO_PPAAM', 'PAYPAL_PPAAM'].includes(paymentMethod)
      ) {
        const adaptedPaymentMethod = paymentMethod === 'VENMO_PPAAM' ? 'Venmo' : 'PayPal';
        result.data.paymentMethod = adaptedPaymentMethod;
        dispatch(onSuccessfulPayment({ result }));
        businessLogic.insight.reportInvoicePayInCP({
          companyInfo,
          insight,
          sale,
          data,
          intuit_tid,
          ssrtid,
          paymentMethod,
          ixp,
          paymentMethodSubType,
        });
      } else {
        handleFailedPayment({ data, intuit_tid, paymentMethod });
      }
    } catch (e) {
      const intuit_tid =
        (e as ErrorWithResponse).config && (e as ErrorWithResponse).config?.headers
          ? (e as ErrorWithResponse).config?.headers['intuit_tid']
          : null;
      handleFailedPayment({ data: {}, intuit_tid, paymentMethod: usedPaymentMethod });
    }
  }
);

export const reportPaymentFailureToPrometheus = (
  businessLogic: IBusinessLogic,
  error: string,
  paymentMethod: string
) => {
  if (businessLogic) {
    try {
      businessLogic.payment.reportPaymentFailure(error, paymentMethod);
    } catch (e) {
      splunkReporter.contextual({
        event: 'payment error',
        action: 'reportPaymentFailure',
        logInfo: { logLevel: 'error', logger },
        activityInfo: { status: 'error' },
        error: { message: (e as Error).message, stack: (e as Error).stack },
      });
    }
  }
};
